
import React, { useState, useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { UserProfile } from '../../types';
import { ProjectCategoryManager } from './ProjectCategoryManager';
import { hasCustomCategories, resolveProjectCategoryDefs } from '../../services/projectCategories';
import { Globe, HardHat, ShieldCheck, Upload, Trash2, Database, Loader2, ArchiveRestore, AlertTriangle, Wand2, RefreshCw, MapPin, LayoutTemplate, Info, ChevronDown, ChevronUp, Maximize2, Minimize2, FileText, Mail, Code, Monitor, Receipt, ListTree, Printer } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { MangelKatalogEditor } from '../mangelKatalog/admin/MangelKatalogEditor';
import { overlayNormalisieren } from '../../services/mangelKatalog/ueberlagerung';
import { setzeKatalogOverlay } from '../../services/mangelKatalog/store';
import { ladeMangelKatalogOhneEigenenBlock } from '../../services/mangelKatalog/laden';
import type { KatalogOverlay } from '../../services/mangelKatalog/typen';
import { SigePlanVorlagenEditor } from './SigePlanVorlagenEditor';
import { PlotterAgentenVerwaltung } from './PlotterAgentenVerwaltung';
import { PlotterPapierVerwaltung } from './PlotterPapierVerwaltung';
import {
    eigenerSigePlanVorlagenBlock, ladeSigePlanVorlagenOhneEigenenBlock, schriftPtText, setzeSigePlanVorlagen,
    useSigePlanVorlagenStand, type SigePlanVorlagen,
} from '../../services/sigePlanVorlagen';
import { schreibvorgangBeginnt } from './einstellungenSchreiben';
import { compressImage } from '../../services/imageService';
import { fileToStationeryImage } from '../../services/stationery';
import { supabase } from '../../services/supabaseClient';
import { ladeAlleSeiten } from '../../services/seitenweise';
import { loadProjectsFromCloud, patchProfileSettings } from '../../services/cloudService';
import { speichereFrischGeladen } from '../../services/frischSpeichern';
import { isProjectSaveConflict } from '../../services/projectVersionAnchor';
import { DEFAULT_INVOICE_CONFIG } from '../../services/invoiceConfig';
import { extractLetterheadConfig } from '../../services/letterheadConfig';
import { composeInvoiceFooterColumns } from '../../services/pdf/companyForPdf';
import { MaintenancePanel } from './MaintenancePanel';

/**
 * Beschriftetes Eingabefeld fuer die Rechnungseinstellungen.
 *
 * Modulweit definiert, nicht in der Komponente: eine im Rendern erzeugte
 * Komponente ist bei jedem Durchlauf ein neuer Typ, React haengt das Feld
 * dann aus und wieder ein, und der Schreibfokus geht nach jedem Zeichen
 * verloren.
 */
const SettingField: React.FC<{
    label: string;
    hint?: string;
    children: React.ReactNode;
    wide?: boolean;
}> = ({ label, hint, children, wide }) => (
    <div className={wide ? 'md:col-span-2' : undefined}>
        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{label}</label>
        {children}
        {hint && <p className="mt-1 text-[11px] leading-snug text-slate-400">{hint}</p>}
    </div>
);

const INPUT_CLASS =
    'w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500';

/**
 * Teil der Firmeneinstellungen, den ein Reiter zeigt (Menü-Umbau 19.10.2026).
 *
 * Die Seite war eine einzige lange Liste von Klappkarten; Angebote,
 * Rechnungen, Mängelkatalog, SiGe-Plan-Vorlagen und Wartung stehen jetzt
 * je in einem eigenen Reiter. Die AdminView rendert diese Komponente dafür
 * einmal je Reiter mit `key={abschnitt}`. Aufgeteilt ist NUR die Anzeige:
 * Laden und Speichern laufen in jedem Abschnitt gleich (siehe
 * touchedFieldsRef) — ein Reiter schreibt nur, was in ihm angefasst wurde.
 */
export type EinstellungsAbschnitt = 'allgemein' | 'angebote' | 'rechnungen' | 'maengelkatalog' | 'plotter' | 'wartung';

interface Props {
  currentUser: UserProfile | null;
  onSaveStatusChange: (status: any) => void;
  /** Nur diesen Abschnitt zeigen. Fehlt er, steht alles auf einer Seite wie vor dem Umbau. */
  abschnitt?: EinstellungsAbschnitt;
}

const UNTERNEHMENSWEIT = 'Diese Daten gelten unternehmensweit für alle Koordinatoren.';

/** Kopf der Seite: ohne Abschnitt der alte Gesamttitel, sonst der des Reiters. */
const GESAMT_KOPF = { titel: 'Globale Firmen-Einstellungen', unterzeile: UNTERNEHMENSWEIT, Icon: Globe as LucideIcon };
const ABSCHNITT_KOPF: Record<EinstellungsAbschnitt, { titel: string; unterzeile: string; Icon: LucideIcon }> = {
    allgemein: { titel: 'Allgemein', unterzeile: UNTERNEHMENSWEIT, Icon: Globe },
    angebote: { titel: 'Angebote', unterzeile: UNTERNEHMENSWEIT, Icon: Receipt },
    rechnungen: { titel: 'Rechnungen', unterzeile: UNTERNEHMENSWEIT, Icon: FileText },
    maengelkatalog: { titel: 'Mängelkatalog', unterzeile: UNTERNEHMENSWEIT, Icon: ListTree },
    plotter: { titel: 'SiGe-Plan-Vorlagen', unterzeile: UNTERNEHMENSWEIT, Icon: Printer },
    // Hier gilt nichts „für alle Koordinatoren" — die Karten schalten die
    // Flotte in den Wartungsmodus und greifen in den Bestand ein.
    wartung: { titel: 'Wartung', unterzeile: 'Wartungsmodus und systemweite Bereinigungen.', Icon: Database },
};

// Das exakte "Clean HTML" Layout, angepasst mit Platzhaltern
const DEFAULT_SIGNATURE_HTML = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
</head>
<body style="margin: 0; padding: 0;">

<table border="0" cellpadding="0" cellspacing="0" width="600" style="font-family: Arial, Helvetica, sans-serif; color: #474343; font-size: 11px; line-height: 1.4;">
    
    <tr>
        <td style="padding-bottom: 5px;">
            <strong style="font-size: 14px; color: #000000;">{vorname} {nachname}</strong><br>
            <span style="color: #0032D5; font-size: 11px;">{Position}</span>
        </td>
    </tr>

    <tr>
        <td style="padding-bottom: 15px;">
            <hr style="border: 0; border-top: 1px solid #474343; margin: 5px 0;">
        </td>
    </tr>

    <tr>
        <td style="padding-bottom: 10px;">
            <img src="https://otkrfgysfhohefploofg.supabase.co/storage/v1/object/public/static-assets/Logo.png" alt="GefAS Logo" width="248" style="display: block; border: 0; max-width: 248px;"><br>
            
            <strong style="font-size: 13px;">Gesellschaft für Arbeitssicherheit mbH</strong><br>
            <strong style="letter-spacing: 1px;">Arbeits- und Gesundheitsschutz</strong><br>
            <span style="color: #0032D5; letter-spacing: 1px;">Beratung - Planung - Koordination</span><br>
            SiGe-Koordinatoren - Fachkraft für Arbeitssicherheit
        </td>
    </tr>

    <tr>
        <td style="padding-top: 10px;">
            <table border="0" cellpadding="0" cellspacing="0" width="100%">
                <tr>
                    <td valign="top" width="33%" style="padding-right: 10px;">
                        Norderstraße 23<br>
                        24632 Lentföhrden<br>
                        Tel: {telefon}<br>
                        <a href="mailto:Info@GefAS-mbH.de" style="color: #0032D5; text-decoration: none;">Info@GefAS-mbH.de</a><br>
                        <a href="https://www.gefas-mbh.de/" style="color: #0032D5; text-decoration: none;">www.GefAS-mbH.de</a>
                    </td>

                    <td valign="top" width="33%" style="border-left: 1px solid #474343; padding-left: 10px; padding-right: 10px;">
                        Geschäftsführer:<br>
                        Michael Ehlers und Ronny Ehlers<br><br>
                        <strong>{vorname} {nachname}</strong><br>
                        <a href="mailto:{email}" style="color: #0032D5; text-decoration: none;">{email}</a>
                    </td>

                    <td valign="top" width="33%" style="border-left: 1px solid #474343; padding-left: 10px;">
                        Registergericht:<br>
                        Amtsgericht Kiel, HRB 24447 KI<br><br>
                        USt-ID: DE205957497<br>
                        Sitz der Gesellschaft:<br>
                        Lentföhrden
                    </td>
                </tr>
            </table>
        </td>
    </tr>

    <tr>
        <td style="padding-top: 20px; font-size: 10px; color: #777;">
            <p style="margin: 0 0 10px 0;">
                Diese E-Mail enthält vertrauliche und/oder rechtlich geschützte <span style="color: #0032D5;">Informationen</span>. Wenn Sie nicht der richtige Adressat sind oder diese E-Mail irrtümlich erhalten haben, informieren Sie bitte sofort den Absender und vernichten Sie diese Mail.<br>
                Das unerlaubte Kopieren sowie die unbefugte Weitergabe dieser Mail ist nicht gestattet.
            </p>
            <p style="margin: 0;">
                Bitte beachten Sie auch unsere Datenschutzerklärung für Geschäftspartner, die Sie über unsere <a href="https://www.gefas-mbh.de/impressum-datenschutzerklarung" style="color: #0032D5; text-decoration: none;">Webseite</a> finden.
            </p>
        </td>
    </tr>

</table>

</body>
</html>`;

/**
 * Welche Zustandsfelder in profiles.settings landen und welche in eigene
 * Spalten. Gespeichert wird ausschliesslich, was der Betreiber in DIESER
 * Sitzung angefasst hat (touchedFieldsRef) — alles andere bleibt so, wie es
 * in der Datenbank steht. Das ist der Grund, warum der frueher noetige
 * "|| existingJson.x"-Schutz einzelner Bloecke entfallen konnte: ein nicht
 * angefasstes Feld kommt gar nicht erst in den Patch.
 */
const SETTINGS_FIELDS = [
    'footerText',
    'senderLine',
    'projectNumberConfig',
    'moduleSettings',
    'constructionTypes',
    'projectCategories',
    'emailSignatureTemplate',
    'quoteConfig',
    'letterheadConfig',
    'invoiceConfig',
    // Firmen-Overlay des Mängelkatalogs (MangelKatalogEditor). undefined im
    // Patch entfernt den Schlüssel („Alles auf Standard“).
    'mangelKatalog',
    // Plotter-Vorlagen des SiGe-Plans (SigePlanVorlagenEditor). undefined im
    // Patch entfernt den Schlüssel („Auf Werkseinstellung zurücksetzen“).
    'sigePlanVorlagen',
] as const;

const COLUMN_FIELDS = ['company_name', 'company_address', 'company_logo', 'app_mode'] as const;

export const AdminGlobalSettings: React.FC<Props> = ({ currentUser, onSaveStatusChange, abschnitt }) => {
    const [settings, setSettings] = useState<any>({
        company_name: currentUser?.company_name,
        company_address: currentUser?.company_address,
        // OHNE Rueckfall auf die Anschrift: der Rueckfall stand sonst im
        // Eingabefeld, der Autosave schrieb ihn fest, und ein geleertes Feld
        // fuellte sich beim naechsten Oeffnen selbst wieder. Der Rueckfall
        // gehoert an den Lesepunkt (resolveFooterText), nicht in den Zustand;
        // im Feld zeigt ihn nur noch der Platzhalter.
        footerText: (currentUser as any)?.settings?.footerText,
        senderLine: (currentUser as any)?.settings?.senderLine,
        company_logo: currentUser?.company_logo,
        app_mode: currentUser?.app_mode || 'sigeko',
        projectNumberConfig: (currentUser as any)?.settings?.projectNumberConfig,
        moduleSettings: (currentUser as any)?.settings?.moduleSettings,
        constructionTypes: (currentUser as any)?.settings?.constructionTypes || [],
        projectCategories: (currentUser as any)?.settings?.projectCategories,
        emailSignatureTemplate: (currentUser as any)?.settings?.emailSignatureTemplate || '',
        quoteConfig: (currentUser as any)?.settings?.quoteConfig || { followUpDays: 14, defaultValidityDays: 30, defaultTaxRate: 19, defaultPaymentTerms: '' },
        // Globaler Briefkopf. Solange er noch nie gespeichert wurde, mit dem
        // Altbestand aus quoteConfig vorbelegt: der erste Speichervorgang
        // uebernimmt die bisherigen Werte unveraendert in den neuen Block
        // (das sind die eigenen Werte des Betreibers, keine Standardwerte —
        // der Vorbehalt am invoiceConfig-Kommentar unten greift hier nicht).
        letterheadConfig:
            (currentUser as any)?.settings?.letterheadConfig ??
            extractLetterheadConfig((currentUser as any)?.settings?.quoteConfig),
        // Mängelkatalog: nur der EIGENE Block, normalisiert (nie werfen,
        // Unbekanntes weg); null = kein eigener Katalog. Welcher Block
        // firmenweit wirkt, zeigt der Editor selbst (useKatalogStand).
        mangelKatalog: overlayNormalisieren(currentUser?.settings?.mangelKatalog),
        // SiGe-Plan-Vorlagen: nur der EIGENE Block (normalisiert), undefined =
        // keiner. Nicht mit der Werkseinstellung vorbelegt — aus demselben
        // Grund wie invoiceConfig darunter. Was firmenweit gilt, liest der
        // Abschnitt aus dem Store (useSigePlanVorlagenStand).
        sigePlanVorlagen: eigenerSigePlanVorlagenBlock(currentUser),
        // Bewusst NICHT mit resolveInvoiceConfig vorbelegt: die Standardwerte
        // gelten ohnehin beim Lesen. Wuerde man sie hier einsetzen, schriebe die
        // erste beliebige Aenderung den gesamten Standard in die Datenbank, und
        // spaetere Aenderungen am Standard erreichten diesen Nutzer nie mehr.
        invoiceConfig: (currentUser as any)?.settings?.invoiceConfig || {}
    });
    
    // Mit Abschnitt startet eine Klappkarte offen, wenn sie die einzige ihres
    // Reiters ist: ein Reiter, der nur einen zugeklappten Balken zeigt,
    // kostet einen Klick ohne Gewinn. Im Reiter „SiGe-Plan-Vorlagen" steht
    // daneben noch der Druck-PC (PlotterAgentenVerwaltung) und die Papierrolle
    // (PlotterPapierVerwaltung); beide laden erst beim Aufklappen und bleiben
    // zu — offen startet die Vorlagenkarte, um die es dort geht.
    const [isConstructionTypesExpanded, setIsConstructionTypesExpanded] = useState(false);
    const [isMangelKatalogExpanded, setIsMangelKatalogExpanded] = useState(abschnitt === 'maengelkatalog');
    const [isSigePlanVorlagenExpanded, setIsSigePlanVorlagenExpanded] = useState(abschnitt === 'plotter');
    const [isLetterheadExpanded, setIsLetterheadExpanded] = useState(false);
    const [isQuotesExpanded, setIsQuotesExpanded] = useState(abschnitt === 'angebote');
    const [isInvoicesExpanded, setIsInvoicesExpanded] = useState(abschnitt === 'rechnungen');
    // Die AdminView hält EINE Instanz über alle Einstellungs-Reiter (der
    // lokale Stand muss überleben, siehe dort). Beim Reiterwechsel folgen die
    // Klappen deshalb dem neuen Abschnitt — angepasst im Rendern, nach dem
    // React-Muster „Zustand bei geänderter Prop", nicht in einem Effekt.
    const [klappenFuer, setKlappenFuer] = useState(abschnitt);
    if (klappenFuer !== abschnitt) {
        setKlappenFuer(abschnitt);
        setIsMangelKatalogExpanded(abschnitt === 'maengelkatalog');
        setIsSigePlanVorlagenExpanded(abschnitt === 'plotter');
        setIsQuotesExpanded(abschnitt === 'angebote');
        setIsInvoicesExpanded(abschnitt === 'rechnungen');
    }
    // Bleibt auch im Reiter „Wartung" zu, obwohl sie dort die einzige
    // Klappkarte ist: darin liegt „Alle Reaktivieren", das JEDES
    // abgeschlossene Projekt wieder öffnet — der Knopf soll nicht schon beim
    // Betreten des Reiters griffbereit liegen.
    const [isMaintenanceExpanded, setIsMaintenanceExpanded] = useState(false);
    const [isEmailExpanded, setIsEmailExpanded] = useState(true);
    const [editorTab, setEditorTab] = useState<'code' | 'preview'>('preview');
    const [isEditorFullscreen, setIsEditorFullscreen] = useState(false);

    useState(false);
    const [isCleaning, setIsCleaning] = useState(false);
    useState(false);
    
    const [isRestoring, setIsRestoring] = useState(false);

    const logoInputRef = useRef<HTMLInputElement>(null);
    // Erst nach der ersten echten Eingabe speichern. Ohne diese Sperre schriebe
    // der Effekt unten schon beim Oeffnen der Seite den Anfangsstand zurueck.
    const dirtyRef = useRef(false);
    // Welche Felder seit dem letzten Schreibvorgang angefasst wurden. Nur dafuer
    // gebraucht, dass eine geaenderte Signatur an alle Mitarbeiter verteilt wird.
    const changedFieldsRef = useRef<Set<string>>(new Set());
    // Welche Felder in DIESER Sitzung ueberhaupt je angefasst wurden. Wird nie
    // geleert: nur diese Felder gehen in den Patch, alles andere bleibt in der
    // Datenbank unberuehrt. Getrennt von changedFieldsRef, weil das dort noetige
    // Leeren nach jedem Schreibvorgang sonst spaetere Speicherungen um die
    // frueheren Aenderungen braechte.
    const touchedFieldsRef = useRef<Set<string>>(new Set());

    /**
     * Aendert eine Einstellung im lokalen Stand. Gespeichert wird NICHT hier,
     * sondern von dem Effekt darunter — zwei Sekunden nach der letzten
     * Aenderung, nach dem Muster des Angebotseditors.
     *
     * Der Wert darf eine Funktion sein; dann bekommt sie den bisherigen Wert.
     * Das ist fuer die verschachtelten Konfigurationen wichtig: wuerden die
     * ihren Ausgangsstand aus der Render-Closure lesen, ginge bei zwei schnell
     * hintereinander geaenderten Feldern das erste verloren.
     */
    const updateGlobalSetting = (field: string, value: any) => {
        if (!currentUser) return;
        dirtyRef.current = true;
        changedFieldsRef.current.add(field);
        touchedFieldsRef.current.add(field);
        setSettings((prev: any) => ({
            ...prev,
            [field]: typeof value === 'function' ? value(prev[field]) : value,
        }));
    };

    // Der Schreibvorgang. Bekommt den zu sichernden Stand mit, statt ihn aus der
    // Closure zu lesen — sonst speicherte ein spaet feuernder Zeitgeber einen
    // veralteten Stand.
    const persistSettings = async (snapshot: any) => {
        if (!currentUser) return;
        const fields = new Set(changedFieldsRef.current);
        changedFieldsRef.current.clear();
        // Erst hier, nicht bei jedem Tastenanschlag: sonst steht die Anzeige
        // waehrend des Tippens dauerhaft auf "wird gespeichert", obwohl noch
        // gar nichts geschrieben wurde.
        onSaveStatusChange('saving');
        // Anmelden fuer die AdminView (einstellungenSchreiben.ts) — unmittelbar
        // vor `try`, damit das `finally` unten den Lauf sicher abmeldet.
        const abmelden = schreibvorgangBeginnt();
        try {
            // Nur die angefassten Bloecke. Alles andere in profiles.settings
            // gehoert anderen Oberflaechen (Unterschrift, Kartenfarbe,
            // Projektansicht, Rollen) und wird von patchProfileSettings frisch
            // aus der Datenbank uebernommen — frueher schrieb diese Seite das
            // ganze JSON aus einem Zustand zurueck, der beim Oeffnen der Seite
            // eingefroren wurde und nie nachzog.
            const touched = touchedFieldsRef.current;
            const patch: Record<string, any> = {};
            for (const field of SETTINGS_FIELDS) {
                if (touched.has(field)) patch[field] = snapshot[field];
            }
            const columns: Record<string, any> = {};
            for (const field of COLUMN_FIELDS) {
                if (touched.has(field)) columns[field] = snapshot[field];
            }

            await patchProfileSettings(currentUser.id, patch, columns);

            // „Alles auf Standard“ gespeichert: wirksam ist jetzt ggf. der Katalog
            // eines anderen Admins — neu bestimmen statt beim Standard zu bleiben.
            if (fields.has('mangelKatalog') && !snapshot.mangelKatalog) {
                void ladeMangelKatalogOhneEigenenBlock(currentUser);
            }
            // Dasselbe für die SiGe-Plan-Vorlagen nach „Auf Werkseinstellung
            // zurücksetzen“: gilt der Block eines anderen Admins, soll dieser
            // Client ihn sofort drucken, nicht die Werkseinstellung.
            if (fields.has('sigePlanVorlagen') && !snapshot.sigePlanVorlagen) {
                void ladeSigePlanVorlagenOhneEigenenBlock(currentUser);
            }

            // SPECIAL SYNC: If Signature changed, broadcast to ALL employees
            if (fields.has('emailSignatureTemplate')) {
                await supabase.rpc('sync_company_signature', {
                    signature_html: snapshot.emailSignatureTemplate
                });
            }

            onSaveStatusChange('saved');
        } catch (e: any) {
            console.error("Save settings error", e);
            // Die Felder zurueckgeben, sonst bliebe eine geaenderte Signatur
            // nach einem Fehlschlag beim naechsten Versuch unverteilt.
            fields.forEach((f) => changedFieldsRef.current.add(f));
            onSaveStatusChange('error');
        } finally {
            abmelden();
        }
    };

    // Stand, den der Autosave-Zeitgeber noch nicht geschrieben hat, und die
    // Fassung von persistSettings aus dem letzten Render (aktuelle Props) —
    // beides fuer das Schreiben beim Aushaengen unten.
    const offenRef = useRef<{ stand: typeof settings | null; schreiben: typeof persistSettings }>({
        stand: null,
        schreiben: async () => {},
    });
    useEffect(() => { offenRef.current.schreiben = persistSettings; });

    // Autosave: zwei Sekunden nach der letzten Aenderung, genau wie im
    // Angebotseditor. Der Zeitgeber haengt am STAND, nicht am Tastendruck —
    // jede weitere Eingabe raeumt ihn ueber die Aufraeumfunktion weg und setzt
    // ihn neu, sodass waehrend des Tippens nichts geschrieben wird.
    useEffect(() => {
        if (!dirtyRef.current) return;
        offenRef.current.stand = settings;
        const t = setTimeout(() => {
            offenRef.current.stand = null;
            void persistSettings(settings);
        }, 2000);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings]);

    // Beim Aushängen den Stand schreiben, den der Zeitgeber nicht mehr
    // geschafft hat. Ohne das ging eine Eingabe still verloren, wenn man
    // binnen zwei Sekunden den Reiter oder die Seite wechselte — seit die
    // Einstellungen auf mehrere Reiter verteilt sind, der Normalfall.
    useEffect(() => {
        const kasten = offenRef.current;
        return () => {
            const stand = kasten.stand;
            if (!stand) return;
            kasten.stand = null;
            void kasten.schreiben(stand);
        };
    }, []);

    const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        // cropToPortrait: false — ohne das schneidet compressImage jedes
        // Querformat auf einen zentralen 3:4-Ausschnitt zu. Das ist fuer
        // Begehungsfotos gedacht; ein breites Firmenlogo verliert dabei links
        // und rechts alles ausser dem Mittelstreifen, schon beim Hochladen.
        // keepTransparency: true — sonst bekommt ein freigestelltes PNG-Logo
        // eine weisse Unterlage, die z. B. im SiGe-Plankopf sichtbar ist.
        if (file) { try { const compressed = await compressImage(file, 600, 0.8, { cropToPortrait: false, keepTransparency: true }); updateGlobalSetting('company_logo', compressed); } catch { alert("Fehler beim Laden des Logos"); } }
    };
    
    // Der wirksame Kategorienkatalog: eigener aus dem Profil oder der Standard.
    // Die Pflege (ProjectCategoryManager) schreibt die ganze Liste zurueck;
    // undefined loescht den Schluessel im Patch und stellt den Standard her.
    const kategorienKatalog = resolveProjectCategoryDefs(settings);

    // Mängelkatalog: eigenes Overlay (oder null = Standard). Jede Änderung
    // geht in den Autosave UND sofort in den Katalog-Store, damit Torte und
    // Suche in diesem Client ohne Neuanmeldung den neuen Stand zeigen.
    // „Alles auf Standard“ (null) schreibt undefined: mergeProfileSettings
    // behält den Schlüssel dann zwar im Objekt, JSON.stringify verwirft ihn
    // aber — der Block verschwindet aus profiles.settings, statt als null
    // stehen zu bleiben.
    const mangelKatalog: KatalogOverlay | null = settings.mangelKatalog ?? null;
    const mangelKatalogAendern = (o: KatalogOverlay | null) => {
        if (!currentUser) return;
        updateGlobalSetting('mangelKatalog', o ?? undefined);
        const name = [currentUser.first_name, currentUser.last_name].filter(Boolean).join(' ').trim() || currentUser.email || '';
        setzeKatalogOverlay(o, o ? { id: currentUser.id, name } : null);
    };

    // SiGe-Plan: Plotter-Vorlagen. Angezeigt wird der eigene Block, sonst der
    // firmenweit wirksame — wer ihn ändert, legt damit einen eigenen Block an.
    // Jede Änderung geht in den Autosave UND sofort in den Store: Stempelfeld,
    // Vorschau und Druck in diesem Client zeigen sie ohne Neuanmeldung.
    // „Auf Werkseinstellung zurücksetzen“ schreibt undefined (Schlüssel fällt
    // weg, siehe mangelKatalog oben).
    const sigePlanVorlagenStand = useSigePlanVorlagenStand();
    const eigeneSigePlanVorlagen: SigePlanVorlagen | undefined = settings.sigePlanVorlagen;
    const sigePlanVorlagen: SigePlanVorlagen = eigeneSigePlanVorlagen ?? sigePlanVorlagenStand.vorlagen;
    const sigePlanVorlagenFremd =
        !eigeneSigePlanVorlagen && !!sigePlanVorlagenStand.quelleId && sigePlanVorlagenStand.quelleId !== currentUser?.id;
    const sigePlanVorlagenAendern = (v: SigePlanVorlagen) => {
        if (!currentUser) return;
        updateGlobalSetting('sigePlanVorlagen', v);
        setzeSigePlanVorlagen(v, currentUser.id);
    };
    const sigePlanVorlagenZuruecksetzen = () => {
        if (!currentUser) return;
        updateGlobalSetting('sigePlanVorlagen', undefined);
        setzeSigePlanVorlagen(null, null);
    };

    // Verschachtelte Konfigurationen immer aus dem VORIGEN Stand ableiten, nie
    // aus der Render-Closure: sonst geht bei zwei Aenderungen innerhalb
    // desselben Frames die erste verloren.
    const QUOTE_CONFIG_FALLBACK = { followUpDays: 14, defaultValidityDays: 30, defaultTaxRate: 19, defaultPaymentTerms: '' };
    const updateQuoteConfig = (field: string, value: any) => {
        updateGlobalSetting('quoteConfig', (prev: any) => ({ ...(prev || QUOTE_CONFIG_FALLBACK), [field]: value }));
    };

    // ── Globaler Briefkopf (Angebote + Rechnungen) ──────────────────────────
    const letterheadCfg = settings.letterheadConfig || {};
    const updateLetterheadConfig = (field: string, value: any) => {
        updateGlobalSetting('letterheadConfig', (prev: any) => ({ ...(prev || {}), [field]: value }));
    };

    // ── Rechnungen ──────────────────────────────────────────────────────────
    // Leere Eingaben werden zu undefined statt zu "": die Validierung nach
    // §14 UStG prueft auf einen nicht-leeren Wert, und ein gespeicherter
    // Leerstring saehe im Datensatz aus wie eine getroffene Entscheidung.
    const invoiceCfg = settings.invoiceConfig || {};
    const updateInvoiceConfig = (field: string, value: any) => {
        updateGlobalSetting('invoiceConfig', (prev: any) => ({
            ...(prev || {}),
            [field]: typeof value === 'string' && value.trim() === '' ? undefined : value,
        }));
    };
    // Fusszeilen-Editor (4 Spalten). Spaltentexte laufen wie closingText an
    // der Leer-Normalisierung vorbei — eine geleerte Spalte ist eine
    // Entscheidung (Spalte entfaellt), kein "nicht gepflegt".
    const updateFooterColumn = (i: number, value: string) =>
        updateGlobalSetting('invoiceConfig', (prev: any) => {
            const cols = [...(prev?.footerColumnsText ?? [])];
            while (cols.length < 4) cols.push('');
            cols[i] = value;
            return { ...(prev || {}), footerColumnsText: cols };
        });
    const toggleFooterCustom = (on: boolean) =>
        updateGlobalSetting('invoiceConfig', (prev: any) => ({
            ...(prev || {}),
            footerCustom: on,
            // Beim ersten Einschalten mit dem Automatik-Stand vorbefuellen:
            // der Betreiber startet vom aktuellen Druckbild, nicht bei null.
            footerColumnsText:
                on && !(prev?.footerColumnsText ?? []).some((c: string) => c?.trim())
                    ? (
                          composeInvoiceFooterColumns(
                              { name: settings.company_name, address: settings.company_address },
                              { ...(prev || {}), footerCustom: false },
                          ) ?? []
                      ).map((col) => col.join('\n'))
                    : prev?.footerColumnsText,
        }));

    // closingText laeuft NICHT durch die Leer-zu-undefined-Normalisierung:
    // leer ist hier eine getroffene Entscheidung ("ohne Grussformel"),
    // undefined der Standardgruss — genau die zwei Zustaende, die die
    // Normalisierung sonst zu einem zusammenfallen laesst. Das Feld liesse
    // sich dann nie leeren: jeder Leerstring wuerde zu undefined und der
    // Standard staende sofort wieder im Eingabefeld.
    const updateClosingText = (value: string) =>
        updateGlobalSetting('invoiceConfig', (prev: any) => ({
            ...(prev || {}),
            closingText: value,
        }));
    const updateNumbering = (field: string, value: any) =>
        updateGlobalSetting('invoiceConfig', (prev: any) => ({
            ...(prev || {}),
            numbering: { ...DEFAULT_INVOICE_CONFIG.numbering, ...(prev?.numbering || {}), [field]: value },
        }));
    const updateDatev = (field: string, value: any) =>
        updateGlobalSetting('invoiceConfig', (prev: any) => ({
            ...(prev || {}),
            datev: { ...(prev?.datev || {}), [field]: value },
        }));
    // Die Anzahlungskonten bekommen beim Eintragen einen Zeitstempel
    // (advancePaymentAccountSince). Er ist keine Eingabe, sondern die Grenze
    // zwischen zwei Buchungslogiken: Abschlaege, die VOR ihm exportiert
    // wurden, liegen auf dem Erloeskonto, und ihr Abzug muss die Erloeszeile
    // mindern statt das Anzahlungskonto aufzuloesen (deductedIsAdvance in
    // datevExport.ts).
    //
    // Gestempelt wird genau beim Wechsel von „gar kein Konto“ auf „mindestens
    // eines“ — die Umstellung ist EIN Ereignis fuer den ganzen Mandanten und
    // kein Vorgang je Steuersatz. Wer spaeter eine Kontonummer korrigiert oder
    // ein zweites Konto nachtraegt, verschiebt die Grenze deshalb nicht; sonst
    // wuerden bereits uebergebene Stapel nachtraeglich der falschen Seite
    // zugerechnet.
    const ADVANCE_ACCOUNT_FIELDS = [
        'advancePaymentAccount19',
        'advancePaymentAccount7',
        'advancePaymentAccount0',
    ] as const;
    const updateAdvancePaymentAccount = (
        field: (typeof ADVANCE_ACCOUNT_FIELDS)[number],
        value: string,
    ) =>
        updateGlobalSetting('invoiceConfig', (prev: any) => {
            const datev = { ...(prev?.datev || {}), [field]: value };
            const vorherLeer = !ADVANCE_ACCOUNT_FIELDS.some((f) => prev?.datev?.[f]);
            const jetztGepflegt = ADVANCE_ACCOUNT_FIELDS.some((f) => datev[f]);
            return {
                ...(prev || {}),
                datev:
                    vorherLeer && jetztGepflegt
                        ? { ...datev, advancePaymentAccountSince: new Date().toISOString() }
                        : datev,
            };
        });


    // Briefpapier (Stationery): Upload/Verarbeitung je Slot, Auto-Fuellung von
    // Slot 2 bei 2-seitigem PDF, Groesse-Warnung.
    //
    // Globaler Briefkopf und Rechnungs-Override teilen sich diesen Weg,
    // unterscheiden sich aber im Ziel. Laufzustand und Hinweis tragen deshalb
    // das Ziel mit: sonst drehte sich beim Hochladen fuer die Rechnung auch
    // das Rad an der globalen Karte, und die Groessenwarnung stuende unter
    // der falschen Karte.
    type StationeryTarget = 'letterhead' | 'invoice';
    const [stationeryBusy, setStationeryBusy] = useState<null | `${StationeryTarget}:${'page1' | 'following'}`>(null);
    const [stationeryNote, setStationeryNote] = useState<null | { target: StationeryTarget; text: string }>(null);

    const applyLetterheadPatch = (patch: Record<string, any>) => {
        updateGlobalSetting('letterheadConfig', (prev: any) => ({ ...(prev || {}), ...patch }));
    };
    const applyInvoiceConfigPatch = (patch: Record<string, any>) => {
        updateGlobalSetting('invoiceConfig', (prev: any) => ({ ...(prev || {}), ...patch }));
    };

    const handleStationeryUpload = async (
        target: StationeryTarget,
        slot: 'page1' | 'following',
        file: File | undefined,
    ) => {
        if (!file) return;
        setStationeryNote(null);
        setStationeryBusy(`${target}:${slot}`);
        try {
            const { page1, page2 } = await fileToStationeryImage(file);
            const field = slot === 'page1' ? 'stationeryPage1' : 'stationeryFollowing';
            const patch: Record<string, any> = { [field]: page1 };
            const cfg: any = (target === 'letterhead' ? settings.letterheadConfig : settings.invoiceConfig) || {};
            const label = target === 'letterhead' ? 'Angebots- und Rechnungs-PDFs' : 'Rechnungs-PDFs';
            const notes: string[] = [];
            // 2-seitiges PDF in Slot 1 und beide Slots leer -> Seite 2 automatisch als Folgeseiten.
            if (page2 && slot === 'page1' && !cfg.stationeryPage1 && !cfg.stationeryFollowing) {
                patch.stationeryFollowing = page2;
                notes.push('PDF mit 2 Seiten erkannt — Seite 2 wurde automatisch als Briefpapier für Folgeseiten übernommen.');
            }
            if (target === 'letterhead') applyLetterheadPatch(patch);
            else applyInvoiceConfigPatch(patch);
            const big = [patch.stationeryPage1, patch.stationeryFollowing]
                .filter(Boolean)
                .some((d: string) => d.length > 800 * 1024);
            if (big) notes.push(`Bild ist groß — ${label} werden entsprechend größer.`);
            setStationeryNote(notes.length ? { target, text: notes.join(' ') } : null);
        } catch (e: any) {
            setStationeryNote({ target, text: e?.message || 'Briefpapier konnte nicht verarbeitet werden.' });
        } finally {
            setStationeryBusy(null);
        }
    };

    const insertPlaceholder = (placeholder: string) => {
        const template = settings.emailSignatureTemplate || '';
        updateGlobalSetting('emailSignatureTemplate', template + placeholder);
    };

    const handleResetSignature = () => {
        if (window.confirm("Möchten Sie die Signatur wirklich auf den GefAS-Standard zurücksetzen?")) {
            updateGlobalSetting('emailSignatureTemplate', DEFAULT_SIGNATURE_HTML);
        }
    };

    const handleForceSyncSignature = async () => {
        if (!confirm("Soll die aktuelle Signatur an ALLE Mitarbeiter übertragen werden?")) return;
        try {
            await supabase.rpc('sync_company_signature', { 
                signature_html: settings.emailSignatureTemplate 
            });
            alert("Signatur wurde erfolgreich an alle Mitarbeiter verteilt.");
        } catch (e: any) {
            alert("Fehler beim Verteilen: " + e.message);
        }
    };

    const handleGlobalCleanup = async () => {
        const msg = "GEWALTIGER DATENABGLEICH STARTEN\n\nDieser Vorgang kann nicht rückgängig gemacht werden. Fortfahren?";
        if (!confirm(msg)) return;
        setIsCleaning(true);
        try {
            const projects = await loadProjectsFromCloud(); let count = 0;
            const uebersprungen: string[] = [];
            for (const p of projects) {
                // NIE den Listeneintrag speichern: er traegt nur `metadata`, und
                // gespeichert loeschte er bei JEDEM Projekt Unterlage,
                // Vorankuendigung, Einstellungen usw. (Vorfall 21.10.2026,
                // listenAuszug.ts). Die Liste liefert nur die Kennungen; jedes
                // Projekt wird voll geladen und mit DIESEM Stand als Anker
                // zurueckgeschrieben (frischSpeichern.ts). Speichert jemand
                // dazwischen, wird frisch geladen; bleibt es beim Konflikt,
                // wird das Projekt uebersprungen statt mit der Bergung den
                // fremden Stand zu ueberschreiben.
                try {
                    await speichereFrischGeladen(p.id, ({ report }) => report);
                    count++;
                } catch (e) {
                    if (!isProjectSaveConflict(e)) throw new Error(`Projekt ${p.project_number || p.name || p.id}: ${(e as Error)?.message || e} — Bereinigung abgebrochen (${count} erledigt).`);
                    uebersprungen.push(String(p.project_number || p.name || p.id));
                }
            }
            alert(`${count} Projekte wurden tiefen-gereinigt und synchronisiert.`
                + (uebersprungen.length > 0 ? `\n\nÜbersprungen (gerade von anderer Stelle gespeichert): ${uebersprungen.join(', ')}` : ''));
            onSaveStatusChange('saved');
        } catch (e: any) { alert("Fehler bei der Bereinigung: " + e.message); } finally { setIsCleaning(false); }
    };

    const handleRestoreAllArchives = async () => {
        if (!confirm("WARNUNG: Alle Projekte mit Status 'Abgeschlossen' wieder auf 'Aktiv' setzen. Fortfahren?")) return;
        setIsRestoring(true);
        try {
            // Erst ALLE Treffer seitenweise sammeln, dann schreiben: wer zwischen
            // zwei Seiten reaktiviert, verschiebt den Offset und ueberspringt
            // Projekte. Ohne `.range()` kaeme ab 1000 nur ein Teil an.
            type Abgeschlossen = {
                id: string;
                name: string;
                data: { metadata?: { globalData?: { status?: string } } };
            };
            const finishedProjects = await ladeAlleSeiten<Abgeschlossen>((von, bis) =>
                supabase.from('projects').select('id, data, name')
                    .or('status.eq.finished,data->metadata->globalData->>status.eq.finished')
                    .order('id').range(von, bis));
            if (!finishedProjects || finishedProjects.length === 0) { alert("Keine archivierten Projekte gefunden."); setIsRestoring(false); return; }
            let count = 0;
            for (const p of finishedProjects) {
                const newData = { ...p.data };
                if (newData.metadata && newData.metadata.globalData) newData.metadata.globalData.status = 'active';
                else if (newData.metadata) newData.metadata.globalData = { ...newData.metadata.globalData, status: 'active' };
                const { error: updateError } = await supabase.from('projects').update({ status: 'active', data: newData, updated_at: new Date().toISOString() }).eq('id', p.id);
                if (!updateError) count++;
            }
            alert(`${count} Projekte wurden erfolgreich reaktiviert.`);
            onSaveStatusChange('saved');
        } catch (e: any) { alert("Fehler: " + e.message); onSaveStatusChange('error'); } finally { setIsRestoring(false); }
    };

    const previewHtml = (settings.emailSignatureTemplate || '')
        .replace(/{vorname}/g, currentUser?.first_name || 'Max')
        .replace(/{nachname}/g, currentUser?.last_name || 'Mustermann')
        .replace(/{email}/g, currentUser?.email || 'max@firma.de')
        .replace(/{telefon}/g, currentUser?.phone || '04191 956692')
        .replace(/{Position}/g, currentUser?.job_title || 'Geschäftsführer');

    // Nur die Anzeige hängt am Abschnitt; Hooks, Laden und Speichern laufen
    // oben unbedingt. Ohne Abschnitt ist jeder Block sichtbar (alte Seite).
    const zeige = (a: EinstellungsAbschnitt) => !abschnitt || abschnitt === a;
    const kopf = abschnitt ? ABSCHNITT_KOPF[abschnitt] : GESAMT_KOPF;
    const KopfIcon = kopf.Icon;
    // Der Plotter-Reiter trägt die Druckliste (PlotterPapierVerwaltung) — eine
    // Tabelle, die in 4xl seitlich scrollen müsste. Er nimmt deshalb die volle
    // Breite der AdminView; die Formular-Reiter bleiben schmal.
    const breite = abschnitt === 'plotter' ? '' : 'max-w-4xl';

    return (
        <div className={`bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden ${breite} mx-auto pb-20`}>

            <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center shrink-0">
                <div>
                    <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2"><KopfIcon className="w-5 h-5 text-indigo-600"/> {kopf.titel}</h3>
                    <p className="text-sm text-slate-500 mt-1">{kopf.unterzeile}</p>
                </div>
            </div>

            {/* Trenner (<hr>) stehen nur ZWISCHEN zwei sichtbaren Blöcken: die
                Bedingung an jedem Trenner sagt, in welcher Ansicht vor und
                nach ihm etwas steht — sonst blieben im Reiter verwaiste oder
                doppelte Linien stehen. */}
            <div className="p-8 space-y-8">
                 {zeige('allgemein') && (
                 <div className="space-y-4">
                    <label className="block text-sm font-bold text-slate-700 uppercase tracking-wide">App Modus & Terminologie</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div onClick={() => updateGlobalSetting('app_mode', 'sigeko')} className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${settings.app_mode === 'sigeko' ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-slate-200 hover:border-blue-300'}`}>
                            <div className="flex items-center gap-3 mb-2"><div className={`p-2 rounded-lg ${settings.app_mode === 'sigeko' ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'}`}><HardHat className="w-5 h-5" /></div><span className="font-bold text-slate-800">SiGeKo (Standard)</span></div><p className="text-xs text-slate-500">Deutsche Terminologie nach BaustellV.</p>
                        </div>
                        <div onClick={() => updateGlobalSetting('app_mode', 'hse')} className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${settings.app_mode === 'hse' ? 'border-emerald-500 bg-emerald-50 shadow-sm' : 'border-slate-200 hover:border-blue-300'}`}>
                            <div className="flex items-center gap-3 mb-2"><div className={`p-2 rounded-lg ${settings.app_mode === 'hse' ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-500'}`}><ShieldCheck className="w-5 h-5" /></div><span className="font-bold text-slate-800">HSE (International)</span></div><p className="text-xs text-slate-500">Internationale Terminologie.</p>
                        </div>
                    </div>
                </div>
                )}

                {zeige('allgemein') && <hr className="border-slate-100" />}

                {/* EMAIL SIGNATURE SECTION */}
                {zeige('allgemein') && (
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                    <div 
                        className="p-6 flex justify-between items-center cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => setIsEmailExpanded(!isEmailExpanded)}
                    >
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg"><Mail className="w-5 h-5" /></div>
                            <div>
                                <h4 className="text-base font-bold text-slate-800">E-Mail Signatur Editor</h4>
                                <p className="text-xs text-slate-500">Zentrale Signatur für den Cloud-Versand</p>
                            </div>
                        </div>
                        <div className="text-slate-400">
                            {isEmailExpanded ? <ChevronUp className="w-5 h-5"/> : <ChevronDown className="w-5 h-5"/>}
                        </div>
                    </div>

                    {isEmailExpanded && (
                        <div className="p-6 pt-0 border-t border-slate-100 space-y-4 animate-in slide-in-from-top-2 duration-300">
                            <div className="mt-6">
                                <div className="flex justify-between items-end mb-4">
                                    <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200">
                                        <button onClick={() => setEditorTab('preview')} className={`px-4 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${editorTab === 'preview' ? 'bg-white shadow text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}><Monitor className="w-4 h-4" /> Live-Vorschau</button>
                                        <button onClick={() => setEditorTab('code')} className={`px-4 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${editorTab === 'code' ? 'bg-white shadow text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}><Code className="w-4 h-4" /> HTML Code</button>
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={handleForceSyncSignature} className="text-[11px] font-bold text-blue-500 hover:text-blue-700 flex items-center gap-1 uppercase tracking-wider bg-blue-50 px-2 py-1 rounded border border-blue-100"><RefreshCw className="w-3 h-3"/> Auf alle Mitarbeiter anwenden</button>
                                        <button onClick={handleResetSignature} className="text-[11px] font-bold text-red-500 hover:text-red-700 flex items-center gap-1 uppercase tracking-wider bg-red-50 px-2 py-1 rounded border border-red-100"><RefreshCw className="w-3 h-3"/> Standard laden</button>
                                    </div>
                                </div>

                                {editorTab === 'code' ? (
                                    <div className={`space-y-4 ${isEditorFullscreen ? 'fixed inset-0 z-[100] bg-slate-900 p-8 flex flex-col' : ''}`}>
                                        
                                        {/* HEADER IN FULLSCREEN */}
                                        {isEditorFullscreen && (
                                            <div className="flex justify-between items-center mb-4 pb-4 border-b border-slate-700">
                                                <div className="flex items-center gap-3">
                                                    <Code className="w-6 h-6 text-blue-400" />
                                                    <h2 className="text-white font-bold text-xl">HTML Editor</h2>
                                                </div>
                                                <button onClick={() => setIsEditorFullscreen(false)} className="text-slate-400 hover:text-white flex items-center gap-2 bg-slate-800 px-3 py-1.5 rounded-lg text-xs font-bold">
                                                    <Minimize2 className="w-4 h-4"/> Schließen
                                                </button>
                                            </div>
                                        )}

                                        <div className="flex flex-wrap gap-2 mb-2">
                                            <span className={`text-[11px] font-bold uppercase mr-2 mt-1.5 ${isEditorFullscreen ? 'text-slate-500' : 'text-slate-400'}`}>Variablen:</span>
                                            <button onClick={() => insertPlaceholder('{vorname}')} className={`px-2 py-1 text-[11px] font-bold rounded border transition-colors ${isEditorFullscreen ? 'bg-slate-800 border-slate-700 text-blue-400 hover:bg-slate-700' : 'bg-blue-50 text-blue-600 border-blue-100 hover:bg-blue-100'}`}>Vorname</button>
                                            <button onClick={() => insertPlaceholder('{nachname}')} className={`px-2 py-1 text-[11px] font-bold rounded border transition-colors ${isEditorFullscreen ? 'bg-slate-800 border-slate-700 text-blue-400 hover:bg-slate-700' : 'bg-blue-50 text-blue-600 border-blue-100 hover:bg-blue-100'}`}>Nachname</button>
                                            <button onClick={() => insertPlaceholder('{Position}')} className={`px-2 py-1 text-[11px] font-bold rounded border transition-colors ${isEditorFullscreen ? 'bg-slate-800 border-slate-700 text-emerald-400 hover:bg-slate-700' : 'bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-100'}`}>Position</button>
                                            <button onClick={() => insertPlaceholder('{email}')} className={`px-2 py-1 text-[11px] font-bold rounded border transition-colors ${isEditorFullscreen ? 'bg-slate-800 border-slate-700 text-blue-400 hover:bg-slate-700' : 'bg-blue-50 text-blue-600 border-blue-100 hover:bg-blue-100'}`}>E-Mail</button>
                                            <button onClick={() => insertPlaceholder('{telefon}')} className={`px-2 py-1 text-[11px] font-bold rounded border transition-colors ${isEditorFullscreen ? 'bg-slate-800 border-slate-700 text-blue-400 hover:bg-slate-700' : 'bg-blue-50 text-blue-600 border-blue-100 hover:bg-blue-100'}`}>Telefon</button>
                                        </div>
                                        
                                        <div className="relative flex-1">
                                            <textarea 
                                                rows={isEditorFullscreen ? undefined : 15} 
                                                className={`w-full border rounded-xl px-4 py-3 text-xs font-mono focus:ring-4 outline-none leading-relaxed shadow-inner ${isEditorFullscreen ? 'h-full bg-slate-800 border-slate-700 text-blue-200 focus:ring-blue-500/30' : 'bg-slate-900 border-slate-300 text-blue-300 focus:ring-blue-500/10 focus:border-blue-500'}`} 
                                                value={settings.emailSignatureTemplate || ''} 
                                                onChange={(e) => updateGlobalSetting('emailSignatureTemplate', e.target.value)}
                                                placeholder="<table style='width: 100%;'>...</table>"
                                            />
                                            {!isEditorFullscreen && (
                                                <button 
                                                    onClick={() => setIsEditorFullscreen(true)}
                                                    className="absolute top-2 right-4 flex gap-1 items-center bg-white/10 backdrop-blur px-2 py-1 rounded text-[10px] text-white/60 border border-white/5 uppercase font-bold tracking-widest hover:bg-white/20 hover:text-white transition-colors"
                                                >
                                                    <Maximize2 className="w-3 h-3" /> Fullscreen
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="bg-gray-200 p-8 rounded-2xl border border-slate-300 shadow-inner min-h-[400px] flex justify-center items-start">
                                        
                                        {/* Outlook-Simulation Wrapper */}
                                        <div data-glass="blatt" className="w-full max-w-[800px] bg-white shadow-2xl rounded-sm overflow-hidden">
                                            {/* Fake Outlook Header */}
                                            <div className="bg-[#f3f2f1] border-b border-[#e1dfdd] p-2 flex items-center gap-4 text-xs text-[#252423]">
                                                <div className="font-bold">Von:</div>
                                                <div>{currentUser?.email || 'max@firma.de'}</div>
                                            </div>
                                            
                                            {/* Body & Signature Container */}
                                            <div className="p-8 bg-white min-h-[300px]">
                                                <p className="text-sm font-sans text-gray-800 mb-8">
                                                    Sehr geehrte Damen und Herren,<br/><br/>
                                                    anbei erhalten Sie das aktuelle Protokoll.<br/><br/>
                                                    Mit freundlichen Grüßen
                                                </p>
                                                
                                                {/* THE SIGNATURE ITSELF - ISOLATED */}
                                                <div 
                                                    className="signature-preview-container"
                                                    style={{ 
                                                        all: 'initial', 
                                                        fontFamily: 'Arial, Helvetica, sans-serif',
                                                        display: 'block'
                                                    }}
                                                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml) }}
                                                />
                                            </div>
                                        </div>

                                    </div>
                                )}
                                
                                <div className="mt-4 flex items-start gap-3 bg-blue-50 p-4 rounded-xl border border-blue-100">
                                    <Info className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                                    <p className="text-xs text-blue-700 leading-relaxed">
                                        <strong>Hinweis:</strong> Die Vorschau simuliert die Ansicht in einem E-Mail-Programm (z.B. Outlook). 
                                        Der gezeigte Rahmen und der Beispieltext werden nicht mitgesendet.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                )}

                {zeige('allgemein') && <hr className="border-slate-100" />}

                {zeige('allgemein') && (
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                    <div className="p-6 flex justify-between items-center cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => setIsConstructionTypesExpanded(!isConstructionTypesExpanded)}>
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-purple-50 text-purple-600 rounded-lg"><LayoutTemplate className="w-5 h-5" /></div>
                            <div>
                                <h4 className="text-base font-bold text-slate-800">Projekt-Kategorisierung</h4>
                                <p className="text-xs text-slate-500">
                                    {kategorienKatalog.length} Kategorien · {kategorienKatalog.reduce((n, c) => n + c.subs.length, 0)} Unterkategorien · {(settings.constructionTypes || []).length} Bauarten
                                </p>
                            </div>
                        </div>
                        <div className="text-slate-400">{isConstructionTypesExpanded ? <ChevronUp className="w-5 h-5"/> : <ChevronDown className="w-5 h-5"/>}</div>
                    </div>
                    {isConstructionTypesExpanded && (
                        <div className="p-6 pt-0 border-t border-slate-100 bg-white">
                            <ProjectCategoryManager
                                categories={kategorienKatalog}
                                isCustom={hasCustomCategories(settings)}
                                onChangeCategories={(list) => updateGlobalSetting('projectCategories', list)}
                                constructionTypes={settings.constructionTypes || []}
                                onChangeConstructionTypes={(list) => updateGlobalSetting('constructionTypes', list)}
                            />
                        </div>
                    )}
                </div>
                )}

                {/* Ohne Abschnitt vor dem Mängelkatalog, im Reiter „Allgemein"
                    vor dem Briefkopf — beide Male steht davor die
                    Kategorisierung. */}
                {zeige('allgemein') && <hr className="border-slate-100" />}

                {/* MÄNGELKATALOG (Standardmängel für Torte, Kacheln und Suche) */}
                {zeige('maengelkatalog') && (
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                    <div className="p-6 flex justify-between items-center cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => setIsMangelKatalogExpanded(!isMangelKatalogExpanded)}>
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-orange-50 text-orange-600 rounded-lg"><ListTree className="w-5 h-5" /></div>
                            <div>
                                <h4 className="text-base font-bold text-slate-800">Mängelkatalog (Standardmängel)</h4>
                                <p className="text-xs text-slate-500">
                                    {mangelKatalog ? 'Eigener Katalog' : 'Standardkatalog'} · ausblenden, Texte ändern, eigene Einträge, Reihenfolge
                                </p>
                            </div>
                        </div>
                        <div className="text-slate-400">{isMangelKatalogExpanded ? <ChevronUp className="w-5 h-5"/> : <ChevronDown className="w-5 h-5"/>}</div>
                    </div>
                    {isMangelKatalogExpanded && (
                        <div className="p-6 pt-0 border-t border-slate-100 bg-white">
                            <MangelKatalogEditor
                                overlay={mangelKatalog}
                                onChange={mangelKatalogAendern}
                                eigeneProfilId={currentUser?.id}
                            />
                        </div>
                    )}
                </div>
                )}

                {!abschnitt && <hr className="border-slate-100" />}

                {/* SiGe-PLAN: PLOTTER-VORLAGEN (Breiten, Standardbreite, Standardschrift) */}
                {zeige('plotter') && (
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                    <div className="p-6 flex justify-between items-center cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => setIsSigePlanVorlagenExpanded(!isSigePlanVorlagenExpanded)}>
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-amber-50 text-amber-600 rounded-lg"><Printer className="w-5 h-5" /></div>
                            <div>
                                <h4 className="text-base font-bold text-slate-800">SiGe-Plan: Plotter-Vorlagen</h4>
                                <p className="text-xs text-slate-500">
                                    {sigePlanVorlagen.breiten.length} {sigePlanVorlagen.breiten.length === 1 ? 'Breite' : 'Breiten'} · Standard {sigePlanVorlagen.standardBreite} mm · Schrift {schriftPtText(sigePlanVorlagen.standardSchriftPt)} · {eigeneSigePlanVorlagen ? 'eigene Vorlage' : sigePlanVorlagenFremd ? 'Vorlage eines anderen Admins' : 'Werkseinstellung'}
                                </p>
                            </div>
                        </div>
                        <div className="text-slate-400">{isSigePlanVorlagenExpanded ? <ChevronUp className="w-5 h-5"/> : <ChevronDown className="w-5 h-5"/>}</div>
                    </div>
                    {isSigePlanVorlagenExpanded && (
                        <div className="p-6 pt-0 border-t border-slate-100 bg-white">
                            <SigePlanVorlagenEditor
                                wert={sigePlanVorlagen}
                                hinweis={sigePlanVorlagenFremd ? 'Derzeit gilt die Vorlage eines anderen Admins; eine Änderung hier legt eine eigene an.' : null}
                                onChange={sigePlanVorlagenAendern}
                                onZuruecksetzen={sigePlanVorlagenZuruecksetzen}
                            />
                        </div>
                    )}
                </div>
                )}

                {zeige('plotter') && <hr className="border-slate-100" />}

                {/* SiGe-PLAN: DRUCK-PC FÜR DEN PLOTTER (Druck-Agent anmelden, sperren).
                    Gehört zum Plotter, deshalb in den Reiter der Vorlagen. */}
                {zeige('plotter') && <PlotterAgentenVerwaltung />}

                {/* PLOTTER: PAPIERROLLE UND DRUCKLISTE (Rollenstand, Drucke mit Länge — SiGe-Plan und Vorankündigung).
                    Ohne Trennlinie direkt unter dem Druck-PC — beide gehören zum Plotter. */}
                {zeige('plotter') && <PlotterPapierVerwaltung />}

                {!abschnitt && <hr className="border-slate-100" />}

                {/* BRIEFKOPF & BRIEFPAPIER (GLOBAL) */}
                {zeige('allgemein') && (
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                    <div className="p-6 flex justify-between items-center cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => setIsLetterheadExpanded(!isLetterheadExpanded)}>
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-sky-50 text-sky-600 rounded-lg"><LayoutTemplate className="w-5 h-5" /></div>
                            <div>
                                <h4 className="text-base font-bold text-slate-800">Briefkopf &amp; Briefpapier</h4>
                                <p className="text-xs text-slate-500">Gilt für Angebote und Rechnungen — Rechnungen können unter „Rechnungen" einen eigenen Briefkopf setzen</p>
                            </div>
                        </div>
                        <div className="text-slate-400">{isLetterheadExpanded ? <ChevronUp className="w-5 h-5"/> : <ChevronDown className="w-5 h-5"/>}</div>
                    </div>
                    {isLetterheadExpanded && (
                        <div className="p-6 pt-0 space-y-4 border-t border-slate-100 bg-white">
                            <div className="mt-4">
                                <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Schriftgröße Fußzeile (pt)</label>
                                <input type="number" min={5} max={12} step={0.5} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-500" value={letterheadCfg.footerFontSize ?? 7} onChange={(e) => updateLetterheadConfig('footerFontSize', Number(e.target.value))} />
                            </div>
                            <div>
                                <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Briefkopf-Infoblock (rechte Spalte auf Seite 1)</label>
                                <textarea rows={6} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-500 resize-none" placeholder={"z.B.\nSonderfachleute für Arbeits- und Gesundheitsschutz\nTel.: 0 41 91 / 95 66 92\nMail: info@gefas-mbh.de\nIBAN: DE...\nSteuernummer: ..."} value={letterheadCfg.letterheadText || ''} onChange={(e) => updateLetterheadConfig('letterheadText', e.target.value)} />
                                <p className="text-[11px] text-slate-400 mt-1">Eine Zeile pro Angabe — erscheint klein rechts oben auf Seite 1. Leerzeilen wirken als Gruppentrenner, die erste Zeile wird fett (Firmenname).</p>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-start">
                                <div>
                                    <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Kopfzeile links (z. B. Verbandsmitgliedschaft)</label>
                                    <input type="text" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-500" placeholder="z.B. Mitglied im Verband der Sicherheits- und Gesundheitsschutzkoordinatoren Deutschlands e.V. (V.S.G.K.)" value={letterheadCfg.letterheadLeftLine || ''} onChange={(e) => updateLetterheadConfig('letterheadLeftLine', e.target.value)} />
                                    <p className="text-[11px] text-slate-400 mt-1">Kleine graue Zeile links unter der oberen Trennlinie auf Seite 1. Bricht bei Bedarf auf zwei Zeilen um.</p>
                                </div>
                                <div className="sm:w-28">
                                    <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Schrift (pt)</label>
                                    <input type="number" min={5} max={14} step={0.5} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-500" value={letterheadCfg.letterheadLeftLineFontSize ?? 6.5} onChange={(e) => updateLetterheadConfig('letterheadLeftLineFontSize', Number(e.target.value))} />
                                    <p className="text-[11px] text-slate-400 mt-1">Standard 6,5 pt. Zeilenabstand wächst mit.</p>
                                </div>
                            </div>

                            {/* BRIEFPAPIER / STATIONERY */}
                            <div className="pt-2 border-t border-slate-100">
                                <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1 mt-3">Briefpapier</label>
                                <p className="text-[11px] text-slate-400 mb-3">Firmen-Briefpapier (PDF oder Bild) als Ganzseiten-Hintergrund. Ersetzt den gezeichneten Briefkopf und die Fußzeile im PDF.</p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {([
                                        { slot: 'page1' as const, field: 'stationeryPage1', title: 'Seite 1' },
                                        { slot: 'following' as const, field: 'stationeryFollowing', title: 'Folgeseiten (optional)' },
                                    ]).map(({ slot, field, title }) => {
                                        const value = letterheadCfg[field] as string | undefined;
                                        return (
                                            <div key={slot} className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-xs font-bold text-slate-600">{title}</span>
                                                    {value && (
                                                        <button type="button" onClick={() => updateLetterheadConfig(field, undefined)} className="text-red-500 hover:text-red-600 text-[11px] font-bold flex items-center gap-1">
                                                            <Trash2 className="w-3 h-3" /> Entfernen
                                                        </button>
                                                    )}
                                                </div>
                                                {value ? (
                                                    <img src={value} alt={title} className="w-full max-h-40 object-contain border border-slate-200 rounded bg-white" />
                                                ) : (
                                                    <label className="flex flex-col items-center justify-center gap-1 h-28 border-2 border-dashed border-slate-300 rounded cursor-pointer hover:border-sky-400 hover:bg-white transition-colors text-slate-400">
                                                        {stationeryBusy === `letterhead:${slot}` ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                                                        <span className="text-[11px]">{stationeryBusy === `letterhead:${slot}` ? 'Wird verarbeitet…' : 'PDF, PNG oder JPEG'}</span>
                                                        <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" disabled={stationeryBusy !== null} onChange={(e) => { handleStationeryUpload('letterhead', slot, e.target.files?.[0]); e.target.value = ''; }} />
                                                    </label>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                {stationeryNote?.target === 'letterhead' && (
                                    <p className="text-[11px] text-amber-600 mt-2 flex items-start gap-1"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />{stationeryNote.text}</p>
                                )}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                                    <div>
                                        <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Inhaltsbeginn Seite 1 (mm)</label>
                                        <input type="number" min={0} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-500" value={letterheadCfg.stationeryTopMarginPage1 ?? 50} onChange={(e) => updateLetterheadConfig('stationeryTopMarginPage1', Number(e.target.value))} />
                                    </div>
                                    <div>
                                        <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Inhaltsbeginn Folgeseiten (mm)</label>
                                        <input type="number" min={0} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-500" value={letterheadCfg.stationeryTopMarginFollowing ?? 25} onChange={(e) => updateLetterheadConfig('stationeryTopMarginFollowing', Number(e.target.value))} />
                                    </div>
                                    <div>
                                        <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Unterer Rand (mm)</label>
                                        <input type="number" min={0} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-500" value={letterheadCfg.stationeryBottomMargin ?? 25} onChange={(e) => updateLetterheadConfig('stationeryBottomMargin', Number(e.target.value))} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                )}

                {!abschnitt && <hr className="border-slate-100" />}

                {/* ANGEBOTE / QUOTE CONFIG SECTION */}
                {zeige('angebote') && (
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                    <div className="p-6 flex justify-between items-center cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => setIsQuotesExpanded(!isQuotesExpanded)}>
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg"><Receipt className="w-5 h-5" /></div>
                            <div>
                                <h4 className="text-base font-bold text-slate-800">Angebote</h4>
                                <p className="text-xs text-slate-500">Standardwerte für Angebote und Aufträge</p>
                            </div>
                        </div>
                        <div className="text-slate-400">{isQuotesExpanded ? <ChevronUp className="w-5 h-5"/> : <ChevronDown className="w-5 h-5"/>}</div>
                    </div>
                    {isQuotesExpanded && (
                        <div className="p-6 pt-0 space-y-4 border-t border-slate-100 bg-white">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                                <div>
                                    <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Nachfassen nach (Tagen)</label>
                                    <input type="number" min={0} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500" value={settings.quoteConfig?.followUpDays ?? 14} onChange={(e) => updateQuoteConfig('followUpDays', Number(e.target.value))} />
                                </div>
                                <div>
                                    <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Gültigkeitsdauer (Tage)</label>
                                    <input type="number" min={0} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500" value={settings.quoteConfig?.defaultValidityDays ?? 30} onChange={(e) => updateQuoteConfig('defaultValidityDays', Number(e.target.value))} />
                                </div>
                                <div>
                                    <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Standard-Steuersatz (%)</label>
                                    <input type="number" min={0} step="0.1" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500" value={settings.quoteConfig?.defaultTaxRate ?? 19} onChange={(e) => updateQuoteConfig('defaultTaxRate', Number(e.target.value))} />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Standard-Zahlungsbedingungen</label>
                                <textarea rows={3} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500 resize-none" placeholder="z.B. Zahlbar innerhalb von 14 Tagen ohne Abzug." value={settings.quoteConfig?.defaultPaymentTerms || ''} onChange={(e) => updateQuoteConfig('defaultPaymentTerms', e.target.value)} />
                            </div>
                        </div>
                    )}
                </div>
                )}

                {!abschnitt && <hr className="border-slate-100" />}

                {/* RECHNUNGEN / INVOICE CONFIG SECTION */}
                {zeige('rechnungen') && (
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                    <div className="p-6 flex justify-between items-center cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => setIsInvoicesExpanded(!isInvoicesExpanded)}>
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg"><FileText className="w-5 h-5" /></div>
                            <div>
                                <h4 className="text-base font-bold text-slate-800">Rechnungen</h4>
                                <p className="text-xs text-slate-500">Pflichtangaben nach §14 UStG, Bankverbindung, Nummernkreis, DATEV</p>
                            </div>
                        </div>
                        <div className="text-slate-400">{isInvoicesExpanded ? <ChevronUp className="w-5 h-5"/> : <ChevronDown className="w-5 h-5"/>}</div>
                    </div>
                    {isInvoicesExpanded && (
                        <div className="p-6 pt-0 space-y-6 border-t border-slate-100 bg-white">

                            {/* — Pflichtangaben — */}
                            <div className="mt-4">
                                <h5 className="text-sm font-bold text-slate-700 mb-1">Pflichtangaben des Rechnungsausstellers</h5>
                                <p className="text-xs text-slate-500 mb-3">
                                    §14 Abs. 4 UStG verlangt entweder die Steuernummer oder die
                                    Umsatzsteuer-Identifikationsnummer. Ohne eine der beiden lässt sich
                                    keine Rechnung ausstellen. Diese Felder gehen auch in die
                                    E-Rechnung (XRechnung/ZUGFeRD) — die Angabe im Briefpapiertext
                                    ist nur ein Bild und für den Empfänger nicht auswertbar.
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <SettingField label="Steuernummer" hint="Wie vom Finanzamt vergeben, z.B. 20 299 61150">
                                        <input type="text" className={INPUT_CLASS} value={invoiceCfg.taxNumber || ''} onChange={(e) => updateInvoiceConfig('taxNumber', e.target.value)} />
                                    </SettingField>
                                    <SettingField label="USt-IdNr." hint="Ohne Leerzeichen, z.B. DE205957497 — so verlangt es die E-Rechnung.">
                                        <input type="text" className={INPUT_CLASS} value={invoiceCfg.vatId || ''} onChange={(e) => updateInvoiceConfig('vatId', e.target.value)} />
                                    </SettingField>
                                    <SettingField label="Registergericht">
                                        <input type="text" className={INPUT_CLASS} placeholder="z.B. Amtsgericht Pinneberg" value={invoiceCfg.registerCourt || ''} onChange={(e) => updateInvoiceConfig('registerCourt', e.target.value)} />
                                    </SettingField>
                                    <SettingField label="Handelsregisternummer">
                                        <input type="text" className={INPUT_CLASS} placeholder="z.B. HRB 12345" value={invoiceCfg.registerNumber || ''} onChange={(e) => updateInvoiceConfig('registerNumber', e.target.value)} />
                                    </SettingField>
                                    <SettingField label="Geschäftsführer" hint="Mehrere durch Komma trennen." wide>
                                        <input type="text" className={INPUT_CLASS} value={(invoiceCfg.managingDirectors || []).join(', ')} onChange={(e) => updateInvoiceConfig('managingDirectors', e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean))} />
                                    </SettingField>
                                </div>

                                <p className="text-xs text-slate-500 mt-4 mb-3">
                                    Ansprechpartner, Telefon und E-Mail sind in der XRechnung
                                    <strong> Pflicht</strong> (BR-DE-5 bis BR-DE-7). Im
                                    Briefkopf-Text stehen sie zwar auf dem Papier — das ist aber
                                    eine freie Textspalte, die ein Empfänger maschinell nicht
                                    auswerten kann.
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <SettingField label="Ansprechpartner" hint="Leer: der erste Geschäftsführer wird verwendet.">
                                        <input type="text" className={INPUT_CLASS} value={invoiceCfg.contactName || ''} onChange={(e) => updateInvoiceConfig('contactName', e.target.value)} />
                                    </SettingField>
                                    <SettingField label="Telefon">
                                        <input type="text" className={INPUT_CLASS} placeholder="z.B. 04191 956692" value={invoiceCfg.phone || ''} onChange={(e) => updateInvoiceConfig('phone', e.target.value)} />
                                    </SettingField>
                                    <SettingField label="E-Mail">
                                        <input type="email" className={INPUT_CLASS} placeholder="z.B. info@gefas-mbh.de" value={invoiceCfg.email || ''} onChange={(e) => updateInvoiceConfig('email', e.target.value)} />
                                    </SettingField>
                                    <SettingField label="Website" hint="Nur fürs Druckbild (Fußzeile) — kein Bestandteil der E-Rechnung.">
                                        <input type="text" className={INPUT_CLASS} placeholder="z.B. www.gefas-mbh.de" value={invoiceCfg.website || ''} onChange={(e) => updateInvoiceConfig('website', e.target.value)} />
                                    </SettingField>
                                </div>
                            </div>

                            {/* — Bankverbindung — */}
                            <div>
                                <h5 className="text-sm font-bold text-slate-700 mb-3">Bankverbindung</h5>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <SettingField label="IBAN">
                                        <input type="text" className={INPUT_CLASS} value={invoiceCfg.iban || ''} onChange={(e) => updateInvoiceConfig('iban', e.target.value)} />
                                    </SettingField>
                                    <SettingField label="BIC">
                                        <input type="text" className={INPUT_CLASS} value={invoiceCfg.bic || ''} onChange={(e) => updateInvoiceConfig('bic', e.target.value)} />
                                    </SettingField>
                                    <SettingField label="Bank">
                                        <input type="text" className={INPUT_CLASS} value={invoiceCfg.bankName || ''} onChange={(e) => updateInvoiceConfig('bankName', e.target.value)} />
                                    </SettingField>
                                    <SettingField label="Zahlungsempfänger (Kontoinhaber)" hint="Wie bei der Bank hinterlegt — oft eine andere Schreibweise als der Firmenname. Steht bei den Kontodaten auf der Rechnung und als Kontoinhaber in der E-Rechnung (BT-85). Leer = Firmenname.">
                                        <input type="text" className={INPUT_CLASS} placeholder="z.B. GEFAS Gesellschaft für Arbeitssicherheit mbH" value={invoiceCfg.accountHolder || ''} onChange={(e) => updateInvoiceConfig('accountHolder', e.target.value)} />
                                    </SettingField>
                                </div>
                            </div>

                            {/* — Standardwerte — */}
                            <div>
                                <h5 className="text-sm font-bold text-slate-700 mb-3">Standardwerte</h5>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <SettingField label="Umsatzsteuersatz (%)" hint="Wird beim Anlegen einer Rechnung je Position vorbelegt.">
                                        <input type="number" min={0} max={100} step={0.1} className={INPUT_CLASS} value={(invoiceCfg.defaultTaxRateBp ?? DEFAULT_INVOICE_CONFIG.defaultTaxRateBp) / 100} onChange={(e) => updateInvoiceConfig('defaultTaxRateBp', Math.round(Number(e.target.value) * 100))} />
                                    </SettingField>
                                    <SettingField label="Zahlungsziel (Tage)" hint="Gilt für jede neue Rechnung im Betrieb. Ein abweichendes Ziel wird beim einzelnen Kunden in der Kundenakte hinterlegt; bereits erstellte Belege bleiben unverändert.">
                                        <input type="number" min={0} className={INPUT_CLASS} value={invoiceCfg.defaultPaymentTermsDays ?? DEFAULT_INVOICE_CONFIG.defaultPaymentTermsDays} onChange={(e) => updateInvoiceConfig('defaultPaymentTermsDays', e.target.value === '' ? undefined : Number(e.target.value) || 0)} />
                                    </SettingField>
                                    <SettingField label="Aufbewahrung (Jahre)" hint="§147 AO. Nur eine Warnung im Archiv — es wird nie automatisch gelöscht.">
                                        <input type="number" min={1} className={INPUT_CLASS} value={invoiceCfg.archiveYears ?? DEFAULT_INVOICE_CONFIG.archiveYears} onChange={(e) => updateInvoiceConfig('archiveYears', Number(e.target.value) || 8)} />
                                    </SettingField>
                                    <SettingField label="Zahlungsbedingungen" wide>
                                        <textarea rows={2} className={`${INPUT_CLASS} resize-none`} value={invoiceCfg.defaultPaymentTerms ?? DEFAULT_INVOICE_CONFIG.defaultPaymentTerms} onChange={(e) => updateInvoiceConfig('defaultPaymentTerms', e.target.value)} />
                                    </SettingField>
                                    <SettingField label="Zahlungshinweis" wide hint="Steht auf der Rechnung direkt unter Zahlungsbedingungen und Bankverbindung — z.B. Verwendungszweck oder Skonto. Leer = kein Hinweis. Entfällt auf Erstattungsbelegen.">
                                        <textarea rows={2} className={`${INPUT_CLASS} resize-none`} placeholder="z.B. Bitte geben Sie bei der Überweisung die Rechnungsnummer an." value={invoiceCfg.paymentNoteText || ''} onChange={(e) => updateInvoiceConfig('paymentNoteText', e.target.value)} />
                                    </SettingField>
                                    <SettingField label="Abschlagsabzug">
                                        <select className={INPUT_CLASS} value={invoiceCfg.advanceDeductionMode ?? DEFAULT_INVOICE_CONFIG.advanceDeductionMode} onChange={(e) => updateInvoiceConfig('advanceDeductionMode', e.target.value)}>
                                            <option value="INDIVIDUAL">Einzeln je Abschlagsrechnung</option>
                                            <option value="GROUPED">Zusammengefasst je Steuersatz</option>
                                        </select>
                                    </SettingField>
                                    <SettingField label="Nebenkosten (%)" hint="Zuschlag auf die Honorarsumme, je Steuersatz. Leer oder 0 = kein Zuschlag. Gilt als Vorschlag für neue Belege; am einzelnen Beleg änderbar.">
                                        <input type="number" min={0} max={100} step={0.1} className={INPUT_CLASS} value={invoiceCfg.defaultAncillaryPercent ?? ''} onChange={(e) => updateInvoiceConfig('defaultAncillaryPercent', e.target.value === '' ? undefined : Number(e.target.value))} />
                                    </SettingField>
                                    <SettingField label="Abrechnungsmodell" hint="Kumulativ: jede Rechnung zeigt den gesamten Leistungsstand und zieht die vorherigen Rechnungen ab. Fortschreibend: nur die neuen Mengen des Zeitraums, kein Abzug.">
                                        <select className={INPUT_CLASS} value={invoiceCfg.defaultBillingMode ?? 'PROGRESSIVE'} onChange={(e) => updateInvoiceConfig('defaultBillingMode', e.target.value === 'CUMULATIVE' ? 'CUMULATIVE' : undefined)}>
                                            <option value="PROGRESSIVE">Fortschreibend (nur neue Mengen)</option>
                                            <option value="CUMULATIVE">Kumulativ (Gesamtstand abzüglich Vorrechnungen)</option>
                                        </select>
                                    </SettingField>
                                    <SettingField label="Standard-Überschrift Rechnungen" wide hint="Steht über jedem Beleg, der keine eigene Überschrift trägt — auch über bereits angelegten Entwürfen. Die Belegnummer bleibt sichtbar: sie rückt in die Zeile darunter. Leer = „Rechnung Nr. …“ je Belegart.">
                                        <input type="text" className={INPUT_CLASS} placeholder="z.B. Sicherheits- und Gesundheitsschutzkoordination" value={invoiceCfg.defaultHeaderText || ''} onChange={(e) => updateInvoiceConfig('defaultHeaderText', e.target.value)} />
                                    </SettingField>
                                    <SettingField label="Einleitungstext" wide>
                                        <textarea rows={2} className={`${INPUT_CLASS} resize-none`} value={invoiceCfg.defaultIntroText ?? DEFAULT_INVOICE_CONFIG.defaultIntroText} onChange={(e) => updateInvoiceConfig('defaultIntroText', e.target.value)} />
                                    </SettingField>
                                    <SettingField label="Schlusstext" wide>
                                        <textarea rows={2} className={`${INPUT_CLASS} resize-none`} placeholder="Optional, z.B. Dankesformel" value={invoiceCfg.defaultOutroText || ''} onChange={(e) => updateInvoiceConfig('defaultOutroText', e.target.value)} />
                                    </SettingField>
                                </div>
                                <label className="mt-4 flex items-start gap-2 text-sm text-slate-700">
                                    <input type="checkbox" className="mt-0.5" checked={!!invoiceCfg.smallBusiness} onChange={(e) => updateInvoiceConfig('smallBusiness', e.target.checked)} />
                                    <span>
                                        <span className="font-semibold">Kleinunternehmer nach §19 UStG</span>
                                        <span className="block text-xs text-slate-500">Setzt den Steuersatz auf 0 % und erzwingt den Hinweistext auf jeder Rechnung. Ein hier ausgewiesener Steuerbetrag wäre nach §14c UStG trotzdem geschuldet.</span>
                                    </span>
                                </label>
                                {invoiceCfg.smallBusiness && (
                                    <div className="mt-3">
                                        <SettingField label="Hinweistext §19">
                                            <input type="text" className={INPUT_CLASS} placeholder="Gemäß §19 UStG wird keine Umsatzsteuer berechnet." value={invoiceCfg.smallBusinessNote || ''} onChange={(e) => updateInvoiceConfig('smallBusinessNote', e.target.value)} />
                                        </SettingField>
                                    </div>
                                )}
                            </div>

                            {/* — Briefkopf Rechnungen — */}
                            <div>
                                <h5 className="text-sm font-bold text-slate-700 mb-1">Briefkopf Rechnungen</h5>
                                <label className="flex items-start gap-2 text-sm text-slate-700 mb-3">
                                    <input type="checkbox" className="mt-0.5" checked={!!invoiceCfg.ownLetterhead} onChange={(e) => updateInvoiceConfig('ownLetterhead', e.target.checked)} />
                                    <span>
                                        <span className="font-semibold">Eigener Briefkopf für Rechnungen</span>
                                        <span className="block text-xs text-slate-500">
                                            {/* Mit Abschnitt steht die Karte nicht mehr auf
                                                derselben Seite — dann sagen, in welchem Reiter. */}
                                            Aus: Rechnungen tragen den globalen Briefkopf aus der Karte
                                            „Briefkopf &amp; Briefpapier"{abschnitt ? ' (Reiter „Allgemein")' : ''}. An: es gelten <strong>nur</strong> die
                                            Angaben hier — leer gelassene Felder bleiben auch auf der
                                            Rechnung leer und fallen nicht auf den globalen Briefkopf
                                            zurück. Die Schriftgröße der Fußzeile bleibt global.
                                        </span>
                                    </span>
                                </label>

                                {invoiceCfg.ownLetterhead && (
                                    <div className="space-y-4 border-l-2 border-indigo-100 pl-4">
                                        <SettingField label="Briefkopf-Infoblock (rechte Spalte im Rechnungs-PDF)" hint="Eine Zeile pro Angabe. Leerzeilen wirken als Gruppentrenner, die erste Zeile wird fett (Firmenname).">
                                            <textarea rows={6} className={`${INPUT_CLASS} resize-none`} placeholder={"z.B.\nGefAS mbH\nTel.: 0 41 91 / 95 66 92\nIBAN: DE...\nSteuernummer: ..."} value={invoiceCfg.letterheadText || ''} onChange={(e) => updateInvoiceConfig('letterheadText', e.target.value)} />
                                        </SettingField>
                                        <SettingField label="Kopfzeile links" hint="Kleine graue Zeile links unter der oberen Trennlinie auf Seite 1.">
                                            <input type="text" className={INPUT_CLASS} value={invoiceCfg.letterheadLeftLine || ''} onChange={(e) => updateInvoiceConfig('letterheadLeftLine', e.target.value)} />
                                        </SettingField>

                                        <div className="pt-2 border-t border-slate-100">
                                            <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1 mt-3">Briefpapier der Rechnungen</label>
                                            <p className="text-[11px] text-slate-400 mb-3">Eigenes Briefpapier (PDF oder Bild) als Ganzseiten-Hintergrund. Ersetzt den gezeichneten Briefkopf und die Fußzeile im Rechnungs-PDF. Ohne Seite 1 gilt kein Briefpapier — dann wird der Kopf aus den Angaben oben gezeichnet.</p>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                {([
                                                    { slot: 'page1' as const, field: 'stationeryPage1', title: 'Seite 1' },
                                                    { slot: 'following' as const, field: 'stationeryFollowing', title: 'Folgeseiten (optional)' },
                                                ]).map(({ slot, field, title }) => {
                                                    const value = invoiceCfg[field] as string | undefined;
                                                    return (
                                                        <div key={slot} className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                                                            <div className="flex items-center justify-between mb-2">
                                                                <span className="text-xs font-bold text-slate-600">{title}</span>
                                                                {value && (
                                                                    <button type="button" onClick={() => updateInvoiceConfig(field, undefined)} className="text-red-500 hover:text-red-600 text-[11px] font-bold flex items-center gap-1">
                                                                        <Trash2 className="w-3 h-3" /> Entfernen
                                                                    </button>
                                                                )}
                                                            </div>
                                                            {value ? (
                                                                <img src={value} alt={title} className="w-full max-h-40 object-contain border border-slate-200 rounded bg-white" />
                                                            ) : (
                                                                <label className="flex flex-col items-center justify-center gap-1 h-28 border-2 border-dashed border-slate-300 rounded cursor-pointer hover:border-indigo-400 hover:bg-white transition-colors text-slate-400">
                                                                    {stationeryBusy === `invoice:${slot}` ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                                                                    <span className="text-[11px]">{stationeryBusy === `invoice:${slot}` ? 'Wird verarbeitet…' : 'PDF, PNG oder JPEG'}</span>
                                                                    <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" disabled={stationeryBusy !== null} onChange={(e) => { handleStationeryUpload('invoice', slot, e.target.files?.[0]); e.target.value = ''; }} />
                                                                </label>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            {stationeryNote?.target === 'invoice' && (
                                                <p className="text-[11px] text-amber-600 mt-2 flex items-start gap-1"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />{stationeryNote.text}</p>
                                            )}
                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                                                <div>
                                                    <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Inhaltsbeginn Seite 1 (mm)</label>
                                                    <input type="number" min={0} className={INPUT_CLASS} value={invoiceCfg.stationeryTopMarginPage1 ?? 50} onChange={(e) => updateInvoiceConfig('stationeryTopMarginPage1', Number(e.target.value))} />
                                                </div>
                                                <div>
                                                    <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Inhaltsbeginn Folgeseiten (mm)</label>
                                                    <input type="number" min={0} className={INPUT_CLASS} value={invoiceCfg.stationeryTopMarginFollowing ?? 25} onChange={(e) => updateInvoiceConfig('stationeryTopMarginFollowing', Number(e.target.value))} />
                                                </div>
                                                <div>
                                                    <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Unterer Rand (mm)</label>
                                                    <input type="number" min={0} className={INPUT_CLASS} value={invoiceCfg.stationeryBottomMargin ?? 25} onChange={(e) => updateInvoiceConfig('stationeryBottomMargin', Number(e.target.value))} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* — Nummernkreis — */}
                            <div>
                                <h5 className="text-sm font-bold text-slate-700 mb-1">Nummernkreis</h5>
                                <p className="text-xs text-slate-500 mb-2">
                                    Das Nummernformat ist fest: <span className="font-mono">RE-JJMMTNNN</span> —
                                    Jahr/Monat, Typziffer aus der Projektnummer, laufende Nummer je Monat ab 001.
                                    Es wird von der Datenbank vergeben und ist hier nicht einstellbar.
                                </p>
                                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                                    Nach der ersten ausgestellten Rechnung nicht mehr umstellen. Die
                                    Nummernfolge muss lückenlos oder dokumentiert lückenhaft bleiben
                                    (GoBD); ein Wechsel mitten im Jahr erzeugt eine Lücke, die man dem
                                    Prüfer erklären muss.
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <SettingField label="Zählweise">
                                        <select className={INPUT_CLASS} value={invoiceCfg.numbering?.mode ?? DEFAULT_INVOICE_CONFIG.numbering.mode} onChange={(e) => updateNumbering('mode', e.target.value)}>
                                            <option value="shared">Ein Kreis über alle Belegarten</option>
                                            <option value="per_type">Eigener Kreis je Belegart</option>
                                        </select>
                                    </SettingField>
                                    <SettingField label="Kürzel im gemeinsamen Kreis">
                                        <input type="text" className={INPUT_CLASS} value={invoiceCfg.numbering?.sharedSeries ?? DEFAULT_INVOICE_CONFIG.numbering.sharedSeries} onChange={(e) => updateNumbering('sharedSeries', e.target.value.toUpperCase())} />
                                    </SettingField>
                                </div>
                            </div>

                            {/* — DATEV — */}
                            <div>
                                <h5 className="text-sm font-bold text-slate-700 mb-1">Steuerberater-Export (DATEV / Simba)</h5>
                                <p className="text-xs text-slate-500 mb-3">
                                    Diese Angaben stehen im Kopf des Buchungsstapels. Sie kommen aus
                                    der Kanzlei — bitte dort erfragen und nicht schätzen; ein
                                    falsches Erlöskonto verbucht den Umsatz still auf dem falschen
                                    Konto.
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <SettingField label="Beraternummer">
                                        <input type="text" className={INPUT_CLASS} value={invoiceCfg.datev?.consultantNumber || ''} onChange={(e) => updateDatev('consultantNumber', e.target.value)} />
                                    </SettingField>
                                    <SettingField label="Mandantennummer">
                                        <input type="text" className={INPUT_CLASS} value={invoiceCfg.datev?.clientNumber || ''} onChange={(e) => updateDatev('clientNumber', e.target.value)} />
                                    </SettingField>
                                    <SettingField label="Beginn Wirtschaftsjahr">
                                        <input type="date" className={INPUT_CLASS} value={invoiceCfg.datev?.fiscalYearStart || ''} onChange={(e) => updateDatev('fiscalYearStart', e.target.value)} />
                                    </SettingField>
                                    <SettingField label="Kontenrahmen">
                                        <select className={INPUT_CLASS} value={invoiceCfg.datev?.skr || 'SKR03'} onChange={(e) => updateDatev('skr', e.target.value)}>
                                            <option value="SKR03">SKR03</option>
                                            <option value="SKR04">SKR04</option>
                                        </select>
                                    </SettingField>
                                    {/* Pflichtwert. Ohne ihn schrieb die Kopfzeile des
                                        Buchungsstapels wörtlich „undefined“ und DATEV
                                        lehnte den Import ab — genau so kam der
                                        Produktivexport zustande. Das Feld zeigt deshalb
                                        LEER, wenn nichts gepflegt ist: die frühere
                                        Vorbelegung mit 4 behauptete einen Wert, der nie
                                        gespeichert wurde. Geraten wird hier nichts, die
                                        Länge kommt aus der Kanzlei. */}
                                    <SettingField
                                        label="Sachkontenlänge"
                                        hint="Pflichtangabe für DATEV — ohne sie läuft kein Export. Aus der Kanzlei erfragen: die Länge bestimmt die Stellenzahl der Personenkonten, die immer eine Stelle mehr haben. Neunstellige Debitoren bedeuten also Sachkontenlänge 8."
                                    >
                                        <input
                                            type="number"
                                            min={4}
                                            max={8}
                                            className={INPUT_CLASS}
                                            placeholder="aus der Kanzlei"
                                            value={invoiceCfg.datev?.accountLength ?? ''}
                                            onChange={(e) =>
                                                updateDatev(
                                                    'accountLength',
                                                    e.target.value === ''
                                                        ? undefined
                                                        : Number(e.target.value),
                                                )
                                            }
                                        />
                                    </SettingField>
                                    <SettingField label="Debitoren-Basiskonto" hint="Optional. Die Kundennummer wird angehängt.">
                                        <input type="text" className={INPUT_CLASS} value={invoiceCfg.datev?.debtorAccountBase || ''} onChange={(e) => updateDatev('debtorAccountBase', e.target.value)} />
                                    </SettingField>
                                    <SettingField label="Erlöskonto 19 %">
                                        <input type="text" className={INPUT_CLASS} value={invoiceCfg.datev?.revenueAccount19 || ''} onChange={(e) => updateDatev('revenueAccount19', e.target.value)} />
                                    </SettingField>
                                    <SettingField label="Erlöskonto 7 %">
                                        <input type="text" className={INPUT_CLASS} value={invoiceCfg.datev?.revenueAccount7 || ''} onChange={(e) => updateDatev('revenueAccount7', e.target.value)} />
                                    </SettingField>
                                    <SettingField label="Erlöskonto 0 % / §13b">
                                        <input type="text" className={INPUT_CLASS} value={invoiceCfg.datev?.revenueAccount0 || ''} onChange={(e) => updateDatev('revenueAccount0', e.target.value)} />
                                    </SettingField>
                                    {/* Je Steuersatz ein eigenes Konto, genau wie
                                        oben bei den Erlösen: bei Automatikkonten
                                        steuert das Konto den Steuerschlüssel, ein
                                        Sammelkonto zöge für 7 % dieselbe
                                        Umsatzsteuer wie für 19 %.

                                        Die drei Felder sind EINE Entscheidung,
                                        deshalb steht der Kasten einmal unter der
                                        Gruppe und nicht dreimal am Feld. Leer ist
                                        der Normalfall dieses Betriebs und bleibt
                                        folgenlos — deshalb weist hier auch nichts
                                        auf „unvollständig“ hin. */}
                                    <SettingField label="Anzahlungskonto 19 %">
                                        <input type="text" className={INPUT_CLASS} placeholder="SKR04: 3272" value={invoiceCfg.datev?.advancePaymentAccount19 || ''} onChange={(e) => updateAdvancePaymentAccount('advancePaymentAccount19', e.target.value)} />
                                    </SettingField>
                                    <SettingField label="Anzahlungskonto 7 %">
                                        <input type="text" className={INPUT_CLASS} placeholder="SKR04: 3271" value={invoiceCfg.datev?.advancePaymentAccount7 || ''} onChange={(e) => updateAdvancePaymentAccount('advancePaymentAccount7', e.target.value)} />
                                    </SettingField>
                                    <SettingField label="Anzahlungskonto 0 % / §13b" hint="Nur, falls die Kanzlei dafür ein eigenes Konto führt.">
                                        <input type="text" className={INPUT_CLASS} value={invoiceCfg.datev?.advancePaymentAccount0 || ''} onChange={(e) => updateAdvancePaymentAccount('advancePaymentAccount0', e.target.value)} />
                                    </SettingField>
                                    <div className="md:col-span-3">
                                        <p className="text-xs leading-relaxed text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                            <span className="font-bold">Nur für echte Abschlagsrechnungen — sonst leer lassen.</span>{' '}
                                            Wer wie dieser Betrieb <span className="font-semibold">Teilleistungen</span>{' '}
                                            abrechnet, braucht die drei Felder nicht: eine
                                            Teilleistung ist eine abgegrenzte, abgenommene und
                                            damit <span className="font-semibold">erbrachte</span>{' '}
                                            Leistung (§ 13 Abs. 1 Nr. 1a UStG) und deshalb echter
                                            Erlös. Eine Anzahlung ist das Gegenteil — noch keine
                                            Leistung, nur Geld, und damit eine Verbindlichkeit, die
                                            auf diesen Konten steht, bis die Schlussrechnung sie
                                            auflöst. Leere Felder sind folgenlos: Abschlagsabzüge
                                            kommen dann gar nicht vor, und der Export läuft
                                            unverändert.
                                        </p>
                                        <p className="mt-2 text-xs leading-relaxed text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                            <span className="font-bold">Eintragen ändert die Buchung der eigenen Belege.</span>{' '}
                                            Sobald hier ein Konto steht, buchen Abschlagsrechnungen
                                            nicht mehr auf das Erlöskonto, sondern hierhin, und ihr
                                            Abzug in der Schluss- oder Restrechnung wird dagegen
                                            aufgelöst. Das gilt ab dem nächsten Export für alle
                                            Belege — auch für ältere, die noch einmal exportiert
                                            werden. Der Zeitpunkt des ersten Eintrags wird
                                            vermerkt und trennt die beiden Buchungslogiken; wer
                                            mitten im Wirtschaftsjahr einträgt, hat sie beide in
                                            einem Jahr. Also nichts, was man vorsorglich ausfüllt:
                                            erst mit der Kanzlei abstimmen und die Nummern von
                                            dort nehmen, nicht aus dem Platzhalter raten.
                                        </p>
                                    </div>
                                    {/* Eine FESTSTELLUNG über das Vorsystem, keine
                                        Vorliebe — deshalb steht die Auswahl in der
                                        Vergangenheitsform und nicht als „so soll
                                        gebucht werden“. */}
                                    <SettingField label="Alt-Abschläge im Vorsystem gebucht als" wide>
                                        <select className={INPUT_CLASS} value={invoiceCfg.datev?.externalAdvanceBooking || ''} onChange={(e) => updateDatev('externalAdvanceBooking', e.target.value || undefined)}>
                                            <option value="">— noch nicht geklärt —</option>
                                            <option value="ADVANCE">Als Abschlagsrechnung gebucht (erhaltene Anzahlung)</option>
                                            <option value="PRIOR_INVOICE">Als gewöhnliche Rechnung gebucht (Erlös)</option>
                                        </select>
                                        <p className="mt-2 text-xs leading-relaxed text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                            <span className="font-bold">Eine Feststellung, keine Entscheidung.</span>{' '}
                                            Gemeint sind die externen Alt-Abschläge der
                                            Bestandsprojekte — Belege aus dem Vorsystem, die hier nur
                                            noch als Abzugsposten stehen. Wie sie in der Buchhaltung
                                            liegen, weiß die Anwendung nicht; das beantwortet die
                                            Kanzlei einmal anhand des Altbestands. Solange hier
                                            nichts steht, bricht jeder Export ab, in dem ein solcher
                                            Abzug vorkommt — die Meldung zeigt auf dieses Feld.
                                            Danach nicht mehr umstellen: der Altbestand ist
                                            derselbe geblieben, und zwei Antworten darauf ergeben
                                            zwei Buchungslogiken für dieselben Belege.
                                        </p>
                                        {/* Die Frage ist am Kontoauszug des
                                            Vorsystems beantwortbar — das gehört
                                            ans Feld, sonst landet sie ein zweites
                                            Mal bei der Kanzlei. */}
                                        <p className="mt-2 text-xs leading-relaxed text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                                            <span className="font-bold">So liest man die Antwort ab:</span>{' '}
                                            Auf welchem Konto liegen die Altbelege in der
                                            Buchhaltung? Ein <span className="font-semibold">Erlöskonto</span>{' '}
                                            (Kontenklasse 4 oder 8, bei SKR04 z. B. 4400) heißt
                                            „als gewöhnliche Rechnung gebucht“. Ein{' '}
                                            <span className="font-semibold">Anzahlungskonto</span>{' '}
                                            (SKR04 3271 / 3272) heißt „als Abschlagsrechnung
                                            gebucht“. Wer Teilleistungen abrechnet, hat dort
                                            praktisch immer ein Erlöskonto stehen — eine
                                            Teilleistung ist erbrachte Leistung und damit Erlös,
                                            keine Anzahlung. Für diesen Betrieb hat die Kanzlei
                                            SKR04 4400 genannt, also „als gewöhnliche Rechnung
                                            gebucht“.
                                        </p>
                                        {/* Nur bei dieser Wahl relevant — als
                                            stehender Satz wäre es eine
                                            Aufforderung, die Konten oben zu
                                            füllen, und die braucht hier niemand. */}
                                        {invoiceCfg.datev?.externalAdvanceBooking === 'ADVANCE' && (
                                            <p className="mt-2 text-xs leading-relaxed text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                                Diese Wahl braucht die Anzahlungskonten oben: der
                                                Abzug wird dort aufgelöst, und ohne das Konto zum
                                                jeweiligen Steuersatz bricht der Export ab.
                                            </p>
                                        )}
                                    </SettingField>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                )}

                {!abschnitt && <hr className="border-slate-100" />}

                {/* Bauphase (Wartungsmodus): bewusst NICHT aufklappbar. Waehrend
                    einer laufenden Sperre muss der Stand — Countdown, offene
                    Sperren, Notausgang — ohne Klick sichtbar sein; eine
                    zugeklappte Karte versteckte genau die Information, die
                    vor dem Deploy zaehlt. Die Projektliste kennt diese Ansicht
                    nicht; die Karte liest die Sperren deshalb selbst (Admins
                    duerfen die ganze Tabelle sehen). */}
                {zeige('wartung') && (currentUser?.role === 'admin' || currentUser?.role === 'super_admin') && (
                    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                        <div className="p-6 flex items-center gap-3">
                            <div className="p-2 bg-amber-50 text-amber-600 rounded-lg"><HardHat className="w-5 h-5" /></div>
                            <div>
                                <h4 className="text-base font-bold text-slate-800">Bauphase (Wartungsmodus)</h4>
                                <p className="text-xs text-slate-500">Wartung ankündigen, Flotte herausführen, deployen, wieder öffnen</p>
                            </div>
                        </div>
                        <div className="p-6 pt-0 border-t border-slate-100 bg-slate-50">
                            <div className="mt-4">
                                <MaintenancePanel currentUser={currentUser} projectIds={[]} />
                            </div>
                        </div>
                    </div>
                )}

                {zeige('wartung') && (
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                     <div className="p-6 flex justify-between items-center cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => setIsMaintenanceExpanded(!isMaintenanceExpanded)}>
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-amber-50 text-amber-600 rounded-lg"><Database className="w-5 h-5" /></div>
                            <div>
                                <h4 className="text-base font-bold text-slate-800">Daten-Wartung & Korrektur</h4>
                                <p className="text-xs text-slate-500">Systemweite Bereinigungen</p>
                            </div>
                        </div>
                        <div className="text-slate-400">{isMaintenanceExpanded ? <ChevronUp className="w-5 h-5"/> : <ChevronDown className="w-5 h-5"/>}</div>
                     </div>
                     {isMaintenanceExpanded && (
                        <div className="p-6 pt-0 border-t border-slate-100 bg-slate-50 mt-2">
                             <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                                <div className="bg-white p-6 rounded-xl border border-blue-200 flex flex-col justify-between shadow-sm"><div><h4 className="text-sm font-bold text-blue-900 flex items-center gap-2 mb-2"><Wand2 className="w-4 h-4" /> LV Dubletten-Filter</h4><p className="text-xs text-blue-700 mb-4 leading-relaxed">Säubert alle Leistungsverzeichnisse von Dubletten.</p></div><button onClick={handleGlobalCleanup} disabled={isCleaning} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-2 shadow-sm transition-colors w-full">{isCleaning ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}Bereinigung starten</button></div>
                                <div className="bg-white p-6 rounded-xl border border-red-200 flex flex-col justify-between shadow-sm"><div><h4 className="text-sm font-bold text-red-900 flex items-center gap-2 mb-2"><ArchiveRestore className="w-4 h-4" /> Archiv-Restore</h4><p className="text-xs text-red-800 mb-4 leading-relaxed">Setzt ALLE "Abgeschlossenen" Projekte auf "Aktiv".</p></div><button onClick={handleRestoreAllArchives} disabled={isRestoring} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-2 shadow-sm transition-colors w-full">{isRestoring ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}Alle Reaktivieren</button></div>
                             </div>
                        </div>
                     )}
                </div>
                )}

                {/* Auf der Gesamtseite folgt das Firmendaten-Raster ohne Linie
                    auf die Wartungskarten (wie bisher); im Reiter „Allgemein"
                    folgt es auf den Briefkopf und wird wie jeder andere Block
                    abgesetzt. */}
                {abschnitt === 'allgemein' && <hr className="border-slate-100" />}

                {zeige('allgemein') && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-4">
                        <div><label className="block text-xs font-bold text-slate-500 uppercase mb-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> Firmenanschrift</label><textarea rows={3} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none resize-none" value={settings.company_address || ''} onChange={(e) => updateGlobalSetting('company_address', e.target.value)} /><p className="text-[11px] text-slate-400 mt-1">Zeilenumbrüche bestimmen die Zeilen in der PDF-Fußzeile (z.B. Straße allein, PLZ + Ort zusammen). Die Absenderzeile über dem Anschriftfeld bleibt einzeilig.</p></div>
                        <div><label className="block text-xs font-bold text-slate-500 uppercase mb-1">Absenderzeile (über dem Empfänger)</label><input type="text" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="z.B. GefAS - Gesellschaft für Arbeitssicherheit mbH • Norderstraße 23 • 24632 Lentföhrden" value={settings.senderLine || ''} onChange={(e) => updateGlobalSetting('senderLine', e.target.value)} /><p className="text-[11px] text-slate-400 mt-1">Die kleine Zeile über der Empfängeranschrift — gilt für Angebot und Rechnung. Leer = automatisch „Firmenname • Anschrift".</p></div>
                        {/* Grussformel: steht bewusst HIER und nicht unter "Rechnungen".
                            Gespeichert wird sie zwar in invoiceConfig.closingText, gelesen
                            aber von allen drei Belegarten — in der Rechnungskarte stand sie
                            neben Einleitungs- und Schlusstext, die NUR fuer die Rechnung
                            gelten (das Angebot fuehrt beide am Beleg selbst). Genau daran
                            las sie sich wie eine Rechnungseinstellung. Der Schluessel bleibt,
                            damit kein Bestand wandern muss. */}
                        <div><label className="block text-xs font-bold text-slate-500 uppercase mb-1">Grußformel (Angebot, Rechnung, Gutschrift)</label><input type="text" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none" value={invoiceCfg.closingText ?? 'Mit freundlichem Gruß'} onChange={(e) => updateClosingText(e.target.value)} /><p className="text-[11px] text-slate-400 mt-1">Die Zeile über Unterschrift und Name — gilt für alle drei Belegarten. Leer = keine Grußzeile; Unterschrift, Name und Firmenname bleiben stehen.</p></div>
                        <div><label className="block text-xs font-bold text-slate-500 uppercase mb-1 flex items-center gap-1"><FileText className="w-3 h-3" /> PDF Footer (Fallback)</label><textarea rows={4} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none resize-none" value={settings.footerText ?? ''} placeholder={settings.company_address || ''} onChange={(e) => updateGlobalSetting('footerText', e.target.value)} /><p className="text-[11px] text-slate-400 mt-1">Zentrierter Text — greift nur, wenn keine Spalten-Fußzeile zustande kommt. Leer gelassen gilt die Firmenanschrift (grau angedeutet); einmal geleert bleibt das Feld leer.</p></div>
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1 flex items-center gap-1"><FileText className="w-3 h-3" /> Spalten-Fußzeile (Angebot + Rechnung)</label>
                            <label className="flex items-start gap-2 text-sm text-slate-700 mb-2">
                                <input type="checkbox" className="mt-0.5" checked={!!invoiceCfg.footerCustom} onChange={(e) => toggleFooterCustom(e.target.checked)} />
                                <span>
                                    <span className="font-semibold">Fußzeile manuell bearbeiten</span>
                                    <span className="block text-xs text-slate-500">Aus: automatisch aus den Rechnungs-Stammdaten. An: die vier Spalten unten gelten wörtlich — jede Zeile im Feld ist eine Zeile im PDF, leere Spalten entfallen.</span>
                                </span>
                            </label>
                            {!!invoiceCfg.footerCustom && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {['Spalte 1 — Firma', 'Spalte 2 — Kontakt', 'Spalte 3 — Rechtliches', 'Spalte 4 — Bank'].map((label, i) => (
                                        <div key={label}>
                                            <label className="block text-[11px] font-bold text-slate-400 uppercase mb-0.5">{label}</label>
                                            <textarea rows={6} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs font-mono focus:ring-2 focus:ring-indigo-500 outline-none resize-none" value={invoiceCfg.footerColumnsText?.[i] ?? ''} onChange={(e) => updateFooterColumn(i, e.target.value)} />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div><label className="block text-xs font-bold text-slate-500 uppercase mb-1">Firmenname</label><input type="text" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none font-bold" value={settings.company_name || ''} onChange={(e) => updateGlobalSetting('company_name', e.target.value)} /></div>
                    </div>
                    <div className="space-y-4">
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Firmenlogo</label>
                        <div onClick={() => logoInputRef.current?.click()} className="border-2 border-dashed border-slate-300 rounded-xl bg-slate-50 h-40 flex flex-col items-center justify-center cursor-pointer hover:border-indigo-400 hover:bg-slate-100 transition-colors relative overflow-hidden group">{settings.company_logo ? <img src={settings.company_logo} alt="Logo" className="h-full w-full object-contain p-4" /> : <div className="text-center text-slate-400"><Upload className="w-8 h-8 mx-auto mb-2" /><span className="text-xs font-bold">Logo hochladen</span></div>}<input type="file" ref={logoInputRef} className="hidden" accept="image/*" onChange={handleLogoUpload} />{settings.company_logo && <button onClick={(e) => { e.stopPropagation(); updateGlobalSetting('company_logo', ''); }} className="absolute top-2 right-2 bg-white text-red-500 p-1 rounded shadow-sm hover:bg-red-50 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 transition-opacity"><Trash2 className="w-4 h-4"/></button>}</div>
                    </div>
                </div>
                )}
            </div>
         </div>
    );
};
